/**
 * Offline fallback for sketch.business_rules.
 *
 * CLAUDE.md is emphatic that business rules are DATA, not code — snap angle,
 * numbering direction, closure tolerance, label format, and so on live in the
 * `sketch.business_rules` table and are read at runtime. This file does NOT
 * contradict that. It is the offline-first seed: the values here mirror the
 * rows seeded by the init migration, and exist so the app has something sane to
 * render with before the sync engine has pulled the live rules (or when it is
 * offline and the local cache is empty on first run).
 *
 * The rule of thumb: nothing in the UI should read these constants directly.
 * Everything takes a resolved rules object as input. Once the sync engine
 * exists, it will hydrate that object from IndexedDB (itself synced from the
 * table), and this file becomes the bootstrap default only. Keeping the shape
 * identical to the table's rule_value payloads is what makes that swap seamless.
 *
 * If you change a default here, change the corresponding seed row in
 * supabase/migrations/ in the same commit — they must not drift.
 */
import { ensureInteriorStructure, normalizeInterior } from '../geometry/interior.js';
import { mmToUnits, nearestLegalPanelHeight } from '../geometry/system-holes.js';
import {
  CV_ACCESSORIES,
  CV_COUNTERTOPS,
  CV_MATERIAL_MULTIPLIERS,
  CV_SECTION_CATALOG,
} from './cv-pricing-data.js';

/**
 * @typedef {object} BusinessRules
 * @property {number} snapIncrementDegrees
 * @property {string} wallOneSelectionRule
 * @property {string} numberingDirection
 * @property {string} defaultUnitOfMeasure
 * @property {number} closureToleranceUnits
 * @property {number} defaultCeilingHeightUnits
 * @property {string} measurementLabelFormat
 * @property {number} elevationScalePxPerInch
 * @property {string} elevationPanelOrder
 * @property {number} defaultDoorWidthUnits
 * @property {number} defaultDoorHeightUnits
 * @property {number} doorSwingLeafAllowanceUnits
 * @property {number} bifoldFoldedProjectionUnits
 * @property {number} atticLadderCeilingInsetUnits
 * @property {Array<{offset_from_floor_units:number, style:string, label:string, kind?:string}>} elevationReferenceLines
 */

/** Mirrors the seeded rows in supabase/migrations. */
export const DEFAULT_BUSINESS_RULES = Object.freeze({
  snapIncrementDegrees: 45,
  wallOneSelectionRule: 'bottom_most_horizontal',
  numberingDirection: 'clockwise',
  defaultUnitOfMeasure: 'in',
  closureToleranceUnits: 0.5,
  // At initial cleanup, the drawing is scaled so its longest wall reads this
  // value and that wall becomes the first real measurement. The resulting
  // px/unit scale remains fixed while later wall measurements change geometry.
  defaultLongestWallUnits: 120,
  defaultCeilingHeightUnits: 118,
  measurementLabelFormat: 'inches_fraction',
  elevationScalePxPerInch: 6,
  elevationPanelOrder: 'wall_number_ascending',
  defaultDoorWidthUnits: 30,
  defaultDoorHeightUnits: 80,
  // A nominal door leaf is typically slightly wider than the clear opening
  // inside its casing. The plan swing uses clear opening + this allowance.
  doorSwingLeafAllowanceUnits: 1,
  // Bifold leaves park OUTSIDE the measured space. A single bifold occupies
  // this projection on its selected folded side; a double uses it at both ends.
  bifoldFoldedProjectionUnits: 5,
  // Wall thickness drawn on the plan (outward from the interior/front face).
  // 4.5" ≈ a 2x4 framed wall with drywall. Per-wall override lives on the wall.
  defaultWallThicknessUnits: 4.5,
  // Default length an interior/partition wall sticks INTO the room when added
  // (ADR 0032). A starting point the designer then edits.
  defaultPartitionLengthUnits: 24,
  // Drop-down attic stairs meet the ceiling this far inside the opening from
  // the length-anchor wall side. Admin-maintained because ladder hardware and
  // field convention may change (owner, 2026-08-16).
  atticLadderCeilingInsetUnits: 6,
  // Clear width kept inside each side of the attic opening when drawing the
  // stair-down footprint. Admin-maintained for different stair assemblies.
  atticLadderSideClearanceUnits: 3,
  // Standard section-type labels for the palette (ADR 0034), each a short code
  // shown on the drawing + a name for the tooltip. Admin-editable — extend this
  // list rather than hardcoding codes elsewhere. Default mounting height is to
  // the label's baseline in the elevation.
  wallLabelPresets: [
    { code: 'DH', name: 'Double hang' },
    { code: 'LH', name: 'Long hang' },
    { code: 'MH', name: 'Medium hang' },
    { code: 'SH', name: 'Shelves' },
    { code: 'SS', name: 'Shoe shelves' },
    { code: 'DRW', name: 'Drawers' },
  ],
  defaultWallLabelHeightUnits: 48,
  // ---- wall sections (ADR 0037) --------------------------------------------
  // A section is a typed cabinetry unit (DH, LH, hamper…) with a real width,
  // depth and height, laid out along a wall between vertical panels. The
  // layout arithmetic lives in src/geometry/section-layout.js; these rules
  // parameterize it. All in the room's unit of measure.
  sectionEndRevealUnits: 0.75, // gap kept between the outermost panel and an abutting side wall
  sectionPanelThicknessUnits: 0.75, // vertical panel thickness; adjacent sections share one by default
  sectionMinOpeningUnits: 18, // an auto section squeezed below this is flagged as a removal candidate
  sectionMaxOpeningUnits: 36, // openings never auto-fill wider than this; explicit per-section override allowed
  // TALL is the fallback class, so the global default is the tall depth: 12"
  // (owner, 2026-08-11 — "the tall sections should have a default depth of 12"
  // (not 14)"). Independently confirmed by the catalog: class T stocks 12/16/24
  // and 14 was never one of them. Drawers and hampers carry their own 16" on
  // their presets; base is 22" (class B stocks 16/20/22/24).
  sectionDefaultDepthUnits: 12, // closet-system panel depth, drawn on the plan
  sectionDefaultHeightUnits: 95.25, // matches the 96" nominal panel's ACTUAL height (ADR 0011)
  // HANGING sections need clothes-hanging clearance in FRONT of them: when one
  // abuts a corner, no section (or panel) on the perpendicular wall may sit
  // within this distance of the hanging wall's face (ADR 0038). A connecting
  // shelf (clearance − hang depth wide) bridges the corner gap automatically.
  // The CLEAR SPACE allowed between a wall and the OUTSIDE FACE of the end
  // panel on the neighbouring wall's run, for that panel to still be outlined
  // on this wall's elevation (owner, 2026-08-05). More clear space than this
  // and the panel is far enough down the other wall that it blocks nothing
  // here — drawing it would mislead.
  //
  // Stated in the designer's terms deliberately: the owner's first reading of
  // an opening-edge measurement forced them to add the panel thickness by
  // hand ("if I want 3 inches of space I need to set 3 3/4"), which is the
  // tell that a rule is in the wrong units. The model adds the panel
  // thickness internally; whoever edits this just states the clear space.
  //
  // 3" is the owner's figure. A run that fills its wall has clear space equal
  // to its scribe reveal (~3/4"), so it always shows; the model also floors
  // the value at that reveal so no Admin edit can hide a corner-tight run.
  //
  // Note this is about the PANEL's distance. Whether an obstruction is blocked
  // is a separate question answered by the obstruction's own depth, which by
  // convention records the clear space required in front of the wall rather
  // than the item's thickness — a breaker panel with a swinging door is as deep
  // as its swing.
  abuttingPanelReachUnits: 3,
  // RETIRED as of ADR 0057 (plan §5/4d): hanging clearance now derives from a
  // section's COMPOSITION (has a rod element), not from a code list or the
  // hanging flag. Kept only so a DB row carrying `section_hanging_codes`
  // still resolves without warning; nothing reads it any more.
  sectionHangingCodes: ['DH', 'LH', 'MH'],
  sectionHangClearanceUnits: 24,
  // The corner GAP when NEITHER unit at a corner hangs (owner, 2026-08-09:
  // "a non-hanging corner gap should be set on the admin maintenance table,
  // with a current default of 12\"", answering "getting section overlap in
  // corner when neither is hanging" and "need open space and CSs for when two
  // units in a corner do not include a hanging unit").
  //
  // This is the OPEN SPACE left beyond the corner unit's carcass, NOT a total
  // clearance from the corner: the neighbour's own depth is reserved first (or
  // the two units simply collide, which is the bug), and this gap sits on top
  // of it — so it is exactly the width of the connecting shelf that bridges
  // the corner. The hanging rule above is a TOTAL for the same reason it can
  // be: 24" of clothes projection already exceeds any carcass depth.
  sectionCornerGapUnits: 12,
  // Open wall spans at least this long get their size drawn on the plan
  // (owner, 2026-08-28: "show the size of any open spaces"). Below it a gap
  // is layout slack, not usable wall, and a label would be noise.
  planOpenSpanLabelMinUnits: 6,
  // The hanging CATEGORIES storage rolls up into (owner, 2026-08-04): the
  // trade's three, plus "very short" for kids' clothes, folded pants and short
  // skirts. A hanging TYPE says which category it feeds via `hangCategory`;
  // several types can feed one — a double hang contributes 2× its width to
  // short hang, a single-rod short hang 1×, and they add.
  //
  // Data, not code, like every other configurable thing here: renaming a
  // category or adding a fifth is an Admin edit, not a deploy.
  // Each category now carries its STANDARD HANG LENGTH as a count of 32 mm
  // system holes (owner, 2026-08-11). The owner gave CV's opening heights —
  // short 39.94", medium 52.54", long 65.14" — and converting each to a hang
  // length (opening minus the rod's 2-hole drop, ADR 0062 §7) lands on
  // 30 / 40 / 50 pitches EXACTLY, to two decimals. So the shop's hang ladder is
  // not a table at all: it is the 32 mm system, and `very_short` completes it
  // at 20 pitches (= the 27.34" opening the arithmetic predicts).
  //
  // A COUNT, not a distance, for the same reason as
  // `rod_support_shelf_hole_offset`: change the pitch and the whole ladder
  // moves with it, leaving no stale constant behind.
  hangCategories: [
    { key: 'very_short', label: 'Very short hang', holePitches: 20 },
    { key: 'short', label: 'Short hang', holePitches: 30 },
    { key: 'medium', label: 'Medium hang', holePitches: 40 },
    { key: 'long', label: 'Long hang', holePitches: 50 },
  ],
  // OPEN sections are deliberate empty space (ADR 0037 addendum): they hold
  // their place in the run so auto-fill can't consume it, but they are not
  // cabinetry — no panel is built solely to bound one, and the width caps /
  // narrow warnings don't apply.
  sectionOpenCodes: ['OPEN'],
  // Section type DEFINITIONS (ADR 0039): flags is multi-select from
  // 'hanging' | 'shelving' | 'drawers' (a drawer unit with hanging above is
  // both). 'hanging' drives the clearance rule (ADR 0038). defaultWidthUnits
  // present = new sections of the type arrive LOCKED at that width (standard-
  // size units like hampers). Counts/dimensions are per-section starting
  // points. Admin-maintained via the in-app panel; this mirrors the seed.
  // minWidthUnits: per-type minimum opening, replacing the GLOBAL
  // section_min_opening_units in the narrow check (a 13" double hang is fine).
  // standardWidths: types that come in SET sizes (hampers) — the designer
  // picks from these instead of typing freely.
  // Each preset also carries an `interior` TEMPLATE (ADR 0054, plan §5/4a): the
  // starter composition a new section of the type is stamped with, read
  // TOP → BOTTOM the way src/geometry/interior.js solves it. Templates are
  // DATA, mirrored faithfully from the type's legacy semantics — DH is its two
  // short-hang rods, SS its 7 flat shoe shelves, DRW its four 6.25" drawers
  // (the real CV 6.25×4 bank) over open space. The hang CATEGORY sits on the
  // ROD ELEMENT, not on the type (groundwork for ADR 0051 step 2). A rule set
  // whose presets predate templates (the live DB row, until re-saved) derives
  // the same compositions from flags + counts via defaultInteriorForPreset().
  sectionTypePresets: [
    { code: 'DH', name: 'Double hang', flags: ['hanging'], hangCategory: 'short', hangRods: 2, minWidthUnits: 12, defaultDepthUnits: 12, defaultHeightUnits: 95.25,
      interior: [
        { kind: 'openSpace', autoHangRole: 'top-opening' },
        { kind: 'shelf', fixed: true, autoHangRole: 'top-divider' },
        { kind: 'rod', hangCategory: 'short', autoHangRole: 'upper-rod' },
        { kind: 'shelf', fixed: true, autoHangRole: 'middle-divider' },
        { kind: 'rod', hangCategory: 'short', autoHangRole: 'lower-rod' },
      ] },
    { code: 'LH', name: 'Long hang', flags: ['hanging'], hangCategory: 'long', hangRods: 1, minWidthUnits: 12, defaultDepthUnits: 12, defaultHeightUnits: 95.25,
      interior: [{ kind: 'rod', hangCategory: 'long' }] },
    { code: 'MH', name: 'Medium hang', flags: ['hanging'], hangCategory: 'medium', hangRods: 1, minWidthUnits: 12, defaultDepthUnits: 12, defaultHeightUnits: 95.25,
      interior: [{ kind: 'rod', hangCategory: 'medium' }] },
    // UPPER CABINET (D95). FOUR parts for this one, not three: the field, the
    // preset, a `cvTypeMapping` row, and this comment saying why.
    //
    // `cvTypeMapping.rows` carries LU24/LU36/LU42 — but those are class U with
    // `config: 'Lsh'`, i.e. L-SHELF uppers. There is no plain U row, so an
    // upper needs its mapping added as well as its type. The catalog has the
    // rows to price against: `U_36_12_24_ShOp` through `U_36_16_36_ShDr`.
    //
    // `anchor: 'top'` is the whole point of an upper and no seeded type has
    // used it before. It is wall-hung and top-referenced ALWAYS: its top
    // matches the standard tall panel whether or not there is a platform, so
    // its mounting elevation is DERIVED (top − height) rather than defaulted —
    // the owner's "uppers have no fixed default elevation" (ADR 0040). The
    // path itself is exercised by platform.test.js, stacking.test.js and
    // sketch-model.test.js; what was missing is a type that declares it.
    //
    // `capshelfUnits: 0.75` is the shelf built onto an upper's bottom, so a
    // 36" upper is 36¾" overall (ADR 0041) — without it a stack sits ¾" wrong.
    { code: 'U36', name: 'Upper 36" Shelves', flags: ['shelving'], anchor: 'top',
      capshelfUnits: 0.75, defaultShelves: 2, minWidthUnits: 12,
      defaultDepthUnits: 12, defaultHeightUnits: 36,
      interior: [
        { kind: 'openSpace' },
                { kind: 'shelf', fixed: false }, { kind: 'openSpace' },
        { kind: 'shelf', fixed: false }, { kind: 'openSpace' },
      ] },
    // BASE CABINET (D95). Same three-part shape as the corner card: the bucket
    // needed a sketch type to name, and the seed had none of class B.
    //
    // The dimensions and the anchor are NOT invented here. A pricing test
    // (`space-pricing.test.js`, "contiguous base tops") had been standing up
    // its own `B34S` fixture to work around this type's absence, and that
    // fixture is the product's own statement of what a base is:
    // `anchor: 'countertop'`, 34" tall, 24" deep. Its comment records the
    // owner's decision that the "34-inch" sketch base maps to CV's B_35 rows
    // with no nearest-height badge, which is why `cvTypeMapping` already
    // carries `B34S` at `cvHeightIn: 35`.
    //
    // `anchor: 'countertop'` is load-bearing: on a platform a base keeps its
    // TOP — the work surface stays put and the body shrinks off the bottom
    // (ADR 0040). A floor-anchored base would ride up with the platform and
    // put the counter at the wrong height.
    { code: 'B34S', name: 'Base 34" Shelves', flags: ['shelving'], anchor: 'countertop',
      defaultShelves: 2, minWidthUnits: 12, defaultDepthUnits: 24, defaultHeightUnits: 34,
      interior: [
        { kind: 'openSpace' },
                { kind: 'shelf', fixed: false }, { kind: 'openSpace' },
        { kind: 'shelf', fixed: false }, { kind: 'openSpace' },
      ] },
    // THE N3 FAMILIES (owner, 2026-08-29): "all these sections would need to
    // be available to designers — I don't understand why they would be
    // different than any other section." Seven catalog families no sketch
    // type could reach become placeable here, each shipping WITH its mapping
    // row below (D116: a type ships with its pricing). Heights are the
    // CATALOG's, not invented: FS/FS-2dr/DRW3/DRW-2F rows exist only at the
    // nominal 28.5, DRW-2dr/DRW3-2F only at 35, ESS only at 84 — a preset
    // pinned to a height its family does not price at would badge every
    // placement as a fallback for no reason. The file units carry
    // defaultWidthUnits 18 because 18" is the only width the catalog prices
    // (the section arrives locked there, like a hamper at its standard
    // width). Interiors are EDITABLE STARTING POINTS, not hardware truth —
    // pricing comes from the pinned config, never from the face count,
    // except where configByDrawerCount says otherwise.
    { code: 'B28F', name: 'Base 28" Flat Shelves', flags: ['shelving'], anchor: 'countertop',
      defaultShelves: 2, minWidthUnits: 12, defaultDepthUnits: 24, defaultHeightUnits: 28,
      interior: [
        { kind: 'openSpace' },
                { kind: 'shelf', fixed: true }, { kind: 'openSpace' },
        { kind: 'shelf', fixed: true }, { kind: 'openSpace' },
      ] },
    // B28D and B34D existed only as ADMIN data (the owner's 2026-08-07 data
    // fix) — which migration A deleted. Compiled here so the owner's own
    // types survive any future cache reset.
    { code: 'B28D', name: 'Base 28" Drawers', flags: ['drawers'], anchor: 'countertop',
      defaultDrawers: 3, minWidthUnits: 12, defaultDepthUnits: 24, defaultHeightUnits: 28,
      interior: [
        { kind: 'openSpace' },
        { kind: 'drawerBank', drawers: [{ heightUnits: 8 }, { heightUnits: 8 }, { heightUnits: 8 }] },
      ] },
    { code: 'B34D', name: 'Base 34" Drawers', flags: ['drawers'], anchor: 'countertop',
      defaultDrawers: 4, minWidthUnits: 12, defaultDepthUnits: 24, defaultHeightUnits: 34,
      interior: [
        { kind: 'openSpace' },
        { kind: 'drawerBank', drawers: [{ heightUnits: 7.5 }, { heightUnits: 7.5 }, { heightUnits: 7.5 }, { heightUnits: 7.5 }] },
      ] },
    { code: 'B34DD', name: 'Base 34" Drawer + Doors', flags: ['drawers'], anchor: 'countertop',
      defaultDrawers: 1, minWidthUnits: 12, defaultDepthUnits: 24, defaultHeightUnits: 34,
      interior: [
        { kind: 'openSpace' },
        { kind: 'drawerBank', drawers: [{ heightUnits: 8 }] },
      ] },
    { code: 'B18F', name: 'Base 28" File Drawers', flags: ['drawers'], anchor: 'countertop',
      defaultDrawers: 2, minWidthUnits: 18, defaultWidthUnits: 18, defaultDepthUnits: 24, defaultHeightUnits: 28,
      interior: [
        { kind: 'openSpace' },
        { kind: 'drawerBank', drawers: [{ heightUnits: 11 }, { heightUnits: 11 }] },
      ] },
    { code: 'B18F3', name: 'Base 34" Drawers + Files', flags: ['drawers'], anchor: 'countertop',
      defaultDrawers: 3, minWidthUnits: 18, defaultWidthUnits: 18, defaultDepthUnits: 24, defaultHeightUnits: 34,
      interior: [
        { kind: 'openSpace' },
        { kind: 'drawerBank', drawers: [{ heightUnits: 7 }, { heightUnits: 11 }, { heightUnits: 11 }] },
      ] },
    // Elite shoe shelves carry BOTH flags, exactly as SS does: shelving (it
    // is one) and shoes (C1 excludes shoe shelves from stacking — "including
    // L shelves, but not shoe shelves"). The interior draws generic flat
    // shoe shelves for now: what an Elite shelf LOOKS like is a drawing
    // decision the owner has not been asked, and the config is pinned on the
    // mapping row, so nothing prices off the style. Named seam.
    { code: 'ESS84', name: 'Elite shoe shelves', flags: ['shelving', 'shoes'],
      defaultShelves: 7, minWidthUnits: 12, defaultWidthUnits: 36, defaultDepthUnits: 12, defaultHeightUnits: 83.9,
      interior: [{ kind: 'shoeShelves', style: 'FSS', count: 7 }] },
    // CORNER L-SHELF (D95). The corner pricing bucket could price but not
    // PLACE, because `representativeType` had no sketch type to name. The
    // machinery it needs has existed since ADR 0042 — a section that occupies
    // a corner, lives in one wall's run and reserves a leg of the neighbour's
    // — and hangs entirely off `cornerLShelf`, which no seeded type set.
    //
    // `LT95` is the code the CV catalog already uses for a tall L-shelf
    // (`cvTypeMapping.rows`), and 95.25" is the actual panel behind CV's
    // nominal 95 (`heightAliases`), so a default-height one prices as the
    // exact catalog row rather than a badged nearest-height fallback.
    { code: 'LT95', name: 'L-shelf (tall)', flags: ['shelving'], cornerLShelf: true,
      defaultShelves: 5, minWidthUnits: 12, defaultDepthUnits: 12, defaultHeightUnits: 95.25,
      interior: [
        { kind: 'openSpace' },
                { kind: 'shelf', fixed: false }, { kind: 'openSpace' },
        { kind: 'shelf', fixed: false }, { kind: 'openSpace' },
        { kind: 'shelf', fixed: false }, { kind: 'openSpace' },
        { kind: 'shelf', fixed: false }, { kind: 'openSpace' },
        { kind: 'shelf', fixed: false }, { kind: 'openSpace' },
      ] },
    { code: 'SH', name: 'Shelves', flags: ['shelving'], defaultShelves: 5, defaultDepthUnits: 12, defaultHeightUnits: 95.25,
      interior: [
        { kind: 'openSpace' },
                { kind: 'shelf', fixed: false }, { kind: 'openSpace' },
        { kind: 'shelf', fixed: false }, { kind: 'openSpace' },
        { kind: 'shelf', fixed: false }, { kind: 'openSpace' },
        { kind: 'shelf', fixed: false }, { kind: 'openSpace' },
        { kind: 'shelf', fixed: false }, { kind: 'openSpace' },
      ] },
    { code: 'SS', name: 'Shoe shelves', flags: ['shelving', 'shoes'], defaultShelves: 7, defaultDepthUnits: 12, defaultHeightUnits: 95.25,
      interior: [{ kind: 'shoeShelves', style: 'FSS', count: 7 }] },
    { code: 'DRW', name: 'Drawers', flags: ['drawers'], defaultDrawers: 4, defaultDepthUnits: 16, defaultHeightUnits: 95.25,
      interior: [
        { kind: 'openSpace' },
        { kind: 'drawerBank', drawers: [{ heightUnits: 6.25 }, { heightUnits: 6.25 }, { heightUnits: 6.25 }, { heightUnits: 6.25 }] },
      ] },
    { code: 'HAMP', name: 'Hamper', flags: [], minWidthUnits: 18, standardWidths: [18, 24, 30], defaultWidthUnits: 30, defaultDepthUnits: 16, defaultHeightUnits: 95.25,
      interior: [{ kind: 'openSpace' }] },
    // ANGLED SHELVES (D101). The owner: *"Angled shelves are basically L
    // shelves that have a 45 degree angle on the inside where the two legs
    // meet. the depth of the legs vary (just like L shelves). the width and
    // depth function the same as L shelves."*
    //
    // So this is `LT95` plus one thing: the inside corner is chamfered rather
    // than square. Everything else — two legs, each with its own length AND
    // depth, a second wall, corner occupancy — is ADR 0042's machinery, which
    // `cornerLShelf` already turns on.
    //
    // THAT DESCRIPTION DISSOLVED A QUESTION RATHER THAN ANSWERING IT. The
    // catalog says `depthIn: 12`; a CV parts table said `Depth 24.00`. I read
    // those as two answers to one question and went looking for which was
    // wrong. Neither was: the shape has TWO legs with two depths, and I had
    // supplied the premise that a section has a single depth, then reasoned
    // from my own premise as though it were evidence.
    //
    // Dimensions are the CATALOG's — `T_84_12_24_AngSh` and `T_95_12_24_AngSh`,
    // both 12" deep and 24" wide. Nothing here is read off a type NAME: D111
    // records that in CV *"the original name sticks, regardless of changes
    // made"*, so a name is frozen at creation while the configuration moves on.
    //
    // `angledInsideCorner` is DECLARED BUT NOT YET DRAWN. Nothing in the plan
    // renderer has ever drawn a chamfer, and inventing one is a drawing
    // decision the owner has not been asked. The flag exists so the type is
    // honest about what it is; the geometry is reported and pending.
    { code: 'AT95', name: 'Angled shelves (tall)', flags: ['shelving'],
      cornerLShelf: true, angledInsideCorner: true,
      defaultShelves: 5, minWidthUnits: 12, defaultDepthUnits: 12, defaultHeightUnits: 95.25,
      interior: [
        { kind: 'openSpace' },
                { kind: 'shelf', fixed: false }, { kind: 'openSpace' },
        { kind: 'shelf', fixed: false }, { kind: 'openSpace' },
        { kind: 'shelf', fixed: false }, { kind: 'openSpace' },
        { kind: 'shelf', fixed: false }, { kind: 'openSpace' },
        { kind: 'shelf', fixed: false }, { kind: 'openSpace' },
      ] },
    // WALL BEDS (D95). The bucket priced — `bucket-pricing.test.js` has been
    // asserting its two-row span at $4,494.24 — but could not PLACE, because
    // `representativeType` was null and the seed carried no type of class WB.
    // Same shape as corner, base and upper, plus a fourth part they did not
    // need. See ADR 0099, which also records two seams this card exposed.
    //
    // The dimensions are the CATALOG's, not a reading of the elevation PDFs.
    // `cv-pricing-data.js` carries exactly two WB rows: 50×20×80 `Bd TwH` and
    // 87.7×20×64 `BdQnV`, and the code names decode as
    // CLASS_HEIGHT_DEPTH_WIDTH_CONFIG (confirmed against both rows).
    //
    // `defaultWidthUnits` IS THE FOURTH PART, and it is not decoration.
    // `priceSection` treats class WB as EXACT-ONLY (price-section.js) — no
    // nearest-size fallback, unlike every other family. At any width but 80"
    // or 64" a wall bed does not price at all; it reports "needs pricing".
    // Sections normally take their width from the run slot, so a wall bed
    // would almost never have been quotable. `defaultWidthUnits` makes the
    // section arrive LOCKED at that width (`addSection`) — the mechanism a
    // hamper already uses — and `allowWide` follows from it, because a
    // standard size IS the explicit intent to exceed `sectionMaxOpeningUnits`.
    // Both beds are wider than that 36".
    //
    // `minWidthUnits` equals the standard width rather than sitting below it:
    // there is one catalog row per bed, so any narrower width is unpriceable
    // and offering it would only produce a section that cannot be quoted.
    //
    // No `cvHeightIn` on the mapping rows below, DELIBERATELY. B34S and U36
    // pin it because CV's nominal height differs from the real panel (ADR
    // 0011); here the catalog height and the built height are the same number.
    // Pinning it would price a designer-resized bed at the catalog height —
    // a wrong price for the drawn unit — where leaving it unpinned reports
    // "needs pricing", which is what exact-only is FOR.
    { code: 'WBTWH', name: 'Wall bed — twin, horizontal', flags: [],
      minWidthUnits: 80, standardWidths: [80], defaultWidthUnits: 80,
      defaultDepthUnits: 20, defaultHeightUnits: 50, fixedHeight: true,
      interior: [{ kind: 'openSpace' }] },
    { code: 'WBQNV', name: 'Wall bed — queen, vertical', flags: [],
      minWidthUnits: 64, standardWidths: [64], defaultWidthUnits: 64,
      defaultDepthUnits: 20, defaultHeightUnits: 87.7, fixedHeight: true,
      interior: [{ kind: 'openSpace' }] },
    { code: 'OPEN', name: 'Open space', flags: [], interior: [{ kind: 'openSpace' }] },
  ],
  // Standard drawer-face BANKS from the CV elevations (plan §5/4a): the
  // heights designers actually order, offered as one-tap compositions when the
  // interior editor lands (4c) and used to seed drawer counts today. Data, not
  // code — a new bank is an Admin/rule edit, not a deploy.
  drawerBankPresets: [
    { key: 'standard3', label: 'Standard 3-drawer (7.42 / 8.66 / 8.68)', heights: [7.42, 8.66, 8.68] },
    { key: 'four625', label: 'Four drawer (6¼ × 4)', heights: [6.25, 6.25, 6.25, 6.25] },
    { key: 'file2', label: 'File drawers (12.44 × 2)', heights: [12.44, 12.44] },
  ],
  // Height a drawer defaults to when a count is stated with no bank to copy
  // (matches the four-drawer CV bank face).
  defaultDrawerHeightUnits: 6.25,
  // The STANDARD drawer face heights a designer picks from (owner, 2026-08-09:
  // heights "should be picked from standard drawer face heights, not free
  // typed"). Mirrored EXACTLY from the shop's own SSH drawer calculator —
  // Designer Portal `apps/portal/drawer-capacity.html`, the `drawerHeightOptions`
  // datalist — so the two tools offer one list: 2½" in 1¼" steps to 18¾".
  // Admin-editable data, like the bank presets beside it. Non-standard heights
  // already stored (the CV banks' 7.42 / 8.66 / 8.68 / 12.44) are PRESERVED and
  // offered alongside these in the editor; they are real orders, not typos.
  drawerFaceHeights: [2.5, 3.75, 5, 6.25, 7.5, 8.75, 10, 11.25, 12.5, 13.75, 15, 16.25, 17.5, 18.75],
  // Drawer STACK assumptions, shared verbatim with the SSH drawer tool's
  // `drawerCalcAssumptions` (Designer Portal `apps/portal/quick-tools.js`) so
  // the sketch and the calculator can never disagree about whether a bank fits
  // (owner, 2026-08-09). See drawerStackHeightUnits in src/geometry/interior.js.
  // toe: the base trim the bottom face sits above ("the first drawer is ~3" off
  // the floor"); overlay: face overlay, which sets the reveal (¾ − overlay) at
  // each end of the stack; spacing: air between faces; max: the drawer tool's
  // 48" warning threshold, INCLUDING base trim.
  drawerToeHeightUnits: 2.5,
  drawerOverlayUnits: 0.375,
  drawerVerticalSpacingUnits: 0.125,
  drawerStackMaxHeightUnits: 48,
  // ─── HANDLES (ADR 0064, owner 2026-08-09) ───────────────────────────────
  // The horizontal overlay of a drawer FACE onto the side panels: "the section
  // width plus 1/2" due to the 1/4" overlay on the left and right side
  // panels". So a face is its opening + 2× this, and it draws PROUD of the
  // opening rather than flush inside it.
  //
  // NOT the same number as `drawerOverlayUnits` (3/8") above: that is the
  // VERTICAL overlay onto the shelves at the top and bottom of a face. Two
  // edges, two rules — do not conflate them.
  drawerFaceSideOverlayUnits: 0.25,
  // The handle CATALOG, as data, because the mounting rule keys off the TYPE:
  // a tab pull is top-mounted and everything else is centred. `mounting` is
  // 'centered' | 'topEdge'; `draw` is the elevation's shape hint ('bar' spans
  // its length, 'knob' is a point). A type may also carry its own `lengthMm`,
  // which is the seam the accessory catalog will eventually populate — see
  // handleLengthUnits() in src/geometry/handles.js.
  handleTypes: [
    { key: 'bar', label: 'Bar pull', mounting: 'centered', draw: 'bar' },
    { key: 'knob', label: 'Knob', mounting: 'centered', draw: 'knob' },
    { key: 'tab', label: 'Tab pull', mounting: 'topEdge', draw: 'bar' },
  ],
  // The default is the "standard stainless bar handle" (owner).
  defaultHandleType: 'bar',
  // "standard pulls are 96mm long" — stored METRIC like the System-32 rules,
  // and it is the OVERALL length, not centre-to-centre (owner-confirmed:
  // "overall length is correct"). 96 mm = 3.7795". Worth stating because 96 mm
  // is conventionally a CTC boring spec, and a 96 mm CTC bar measures ~128 mm
  // end to end — a reader assuming the convention would think this is wrong.
  defaultHandleLengthMm: 96,
  // "for drawers >=32" wide, they require two pulls unless the pulls are at
  // least 25% the width of the drawer face". Both figures are rules; the
  // threshold is INCLUSIVE, so exactly 32" is a two-pull face.
  handleTwoPullMinFaceWidthUnits: 32,
  handleSinglePullMinWidthFraction: 0.25,
  // Where a PAIR sits across the face. The owner asked for the quarter points
  // AND asked that they be editable: "use quarter points but put that in the
  // admin maintenance so it can be modified". A LIST, so it is maintained
  // beside drawer_bank_presets rather than in the numbers form.
  handlePairPositions: [0.25, 0.75],
  // How many SYSTEM HOLES separate a rod's top from the hole its covering FIXED
  // shelf is centred on (owner, 2026-08-09: "the fixed shelf would be 2 system
  // holes above the top of the rod (so there is one visible hole between the
  // rod and the fixed shelf)… so for rods below the top shelf, they would be
  // between the 3rd and 4th system hole from the top of the panel").
  //
  // A COUNT, not a distance: two pitches is 64 mm = 2.5196850", which is what
  // the owner's earlier "~2.5 inches" was all along. The solver derives the
  // drop through rodHoleDropUnits(), so changing `system_hole_pitch_mm` moves
  // the rod with it and no constant is left behind (ADR 0062 §7).
  rodSupportShelfHoleOffset: 2,
  // HANG LENGTH's fallback datum (owner, 2026-08-11): "if it is a lower rod and
  // there is no bottom shelf (again, an exception) the measurement would be to
  // 1" off the floor". Also the clearance used when a rod hangs directly over
  // another rod with no board between — in both cases it is the gap a garment
  // needs so it does not foul what is underneath. See rodHangLengthUnits().
  rodHangFloorClearanceUnits: 1,
  // A hang rod's real DIAMETER (owner, 2026-08-09: "rods at 1 1/4" thick as
  // that mirrors actual size/height"). The elevation draws the rod as a body
  // of this height hanging from its top datum, not as a line (ADR 0062 §8).
  // At the shop's 32 mm pitch a 1 1/4" rod all but fills the gap between two
  // system holes (1.2598"), which is the owner's "they sit between two system
  // holes" falling straight out of the two figures.
  hangRodDiameterUnits: 1.25,
  // SUPERSEDED by rodSupportShelfHoleOffset (ADR 0062 §7); kept so a stored
  // rules row carrying it still resolves. Nothing reads it any more.
  rodSupportShelfGapUnits: 2.5,
  // The shallowest section that can take DOORS (ADR 0056; REVISED by ADR 0060,
  // owner 2026-08-09). There is effectively no depth requirement for a
  // NON-HANGING section — "doors can go on any section deeper than 4"" — so 4
  // is a sanity floor, not a real constraint: a 12" shelving unit takes doors.
  // A HANGING section is different, and has its own rule below.
  doorMinDepthUnits: 4,
  // A section whose interior holds a ROD ANYWHERE needs this much depth for
  // doors (owner, 2026-08-09): clothes on a rod need the depth, so a 12" double
  // hang cannot be doored however shallow the door itself is. The test is the
  // WHOLE section's composition, not the door band — adding a rod to a rod-less
  // section raises its minimum from 4" to 24" immediately.
  doorMinDepthHangingUnits: 24,
  // Doors are typically a left/right PAIR (owner, 2026-08-09). The reveal is
  // the gap drawn between the two leaves; an opening narrower than the pair
  // minimum gets a single leaf instead of two doors too narrow to order.
  doorPairCenterRevealUnits: 0.125,
  doorPairMinWidthUnits: 24,
  // Door pricing and fabrication limits are one admin-maintained LIST rule.
  // Category is presentation only; every price and maximum lives on STYLE.
  // The compiled table is authoritative until an admin deliberately creates
  // an SD-118 v2 override row — there is deliberately no duplicate seed row.
  doorStyles: [
    { key: 'melamine-slab', category: 'Melamine', style: 'Slab', ratePerSqFt: 10, maxLeafWidthUnits: 18, maxLeafHeightUnits: 96 },
    { key: 'melamine-3-piece', category: 'Melamine', style: '3 piece', ratePerSqFt: 13, maxLeafWidthUnits: 18, maxLeafHeightUnits: 96 },
    { key: 'melamine-5-piece', category: 'Melamine', style: '5 piece', ratePerSqFt: 15, maxLeafWidthUnits: 18, maxLeafHeightUnits: 56 },
    { key: 'melamine-5-piece-mullions', category: 'Melamine', style: '5 piece mullions', ratePerSqFt: 25, maxLeafWidthUnits: 18, maxLeafHeightUnits: 56 },
    { key: 'thermafoil-raised-panel', category: 'Thermafoil', style: 'Raised panel', ratePerSqFt: 14, maxLeafWidthUnits: 18, maxLeafHeightUnits: 56 },
    { key: 'thermafoil-raised-panel-mullions', category: 'Thermafoil', style: 'Raised panel mullions', ratePerSqFt: 19, maxLeafWidthUnits: 18, maxLeafHeightUnits: 56 },
    { key: 'thermafoil-shaker', category: 'Thermafoil', style: 'Shaker', ratePerSqFt: 14, maxLeafWidthUnits: 18, maxLeafHeightUnits: 56 },
    { key: 'thermafoil-shaker-mullions', category: 'Thermafoil', style: 'Shaker mullions', ratePerSqFt: 19, maxLeafWidthUnits: 18, maxLeafHeightUnits: 56 },
    { key: 'aluminum-frame-af', category: 'Aluminum Frame', style: 'AF', ratePerSqFt: 45, maxLeafWidthUnits: 24, maxLeafHeightUnits: 96 },
    { key: 'aluminum-frame-afoc', category: 'Aluminum Frame', style: 'AFOC', ratePerSqFt: 120, maxLeafWidthUnits: 24, maxLeafHeightUnits: 96 },
  ],
  // Rounding for the storage summary's linear-inch figures (owner, 2026-08-05
  // — it was rounding to the whole inch). These are sums of SOLVED openings, so
  // the last sixteenth is arithmetic rather than anything measured; a quarter
  // is the honest precision. Set to 0 to report the raw figure.
  storageRoundingUnits: 0.25,
  // A pair of shoes occupies this much shelf width — the storage summary
  // computes pairs = shelves × floor(opening / this) for "shoes" types.
  shoePairWidthUnits: 8,
  // The most-used obstruction types get one-tap quick-add buttons (like labels),
  // in addition to the full dropdown (ADR 0034). Keys reference obstructionTypes.
  obstructionQuickKeys: ['outlet', 'light_switch', 'window', 'sprinkler', 'hvac_vent'],
  // To-scale plan/elevation grid cell (ADR 0024).
  gridSpacingUnits: 6,
  // How far BELOW the floor line a section's width caption sits (owner,
  // 2026-08-09: "The width of the section should be shown below the floor, not
  // at the floor, to avoid it being blocked" — base sections and drawer faces
  // were covering it). SCREEN PIXELS, not units: every other caption on the
  // elevation is placed in px off the floor line (the wall name and length
  // already are), and a figure in inches would move with the zoom while the
  // text did not. The wall name/length rows shift down by the same amount, so
  // the below-floor stack stays: widths, wall name, wall length.
  elevationWidthLabelDropPx: 13,
  // Quick depth-line presets offered when a wall is selected (cabinet depths:
  // 12" upper, 16", 24" base). The designer can also type a custom distance.
  planDepthLinePresets: [12, 16, 24],
  // Platform heights the shop offers for the Floor / 3" / 6" toggle (ADR 0040 —
  // garage systems). "Floor" (0) is always available; these are the raised
  // options. Admin-editable data so a new platform height needs no deploy.
  platformHeights: [3, 6],
  // Countertop MATERIALS and the thickness each one comes in (owner,
  // 2026-08-30: "rather than manual, select type w/ presets"). A designer picks
  // the material they are quoting; the thickness follows, because it is a
  // property of the material and not a free choice. `Other` is not listed here
  // — it is the UI's escape hatch, which still takes a typed figure for the
  // occasional slab nobody stocks. Data rather than a constant, so correcting a
  // thickness is an Admin edit, not a release.
  countertopMaterialPresets: [
    { key: 'melamine', label: 'Melamine', thicknessUnits: 0.75 },
    { key: 'thinscape', label: 'Thinscape', thicknessUnits: 0.5 },
    { key: 'quartz', label: 'Quartz', thicknessUnits: 1.125 },
    { key: 'laminate', label: 'Laminate', thicknessUnits: 1.5 },
  ],
  // Crown-molding allowance below the ceiling in "to the ceiling" mode (ADR
  // 0041): units run to ceiling − this, so a 110" ceiling gives 107" of unit.
  crownAllowanceUnits: 3,
  // Visible trim geometry. Pricing and rendering use the same maintained
  // dimensions, so the elevation is an honest preview of the selected trim.
  crownMouldingHeightUnits: 3,
  baseTrimHeightUnits: 2.5,
  baseTrimShelfOverlapRatio: 1 / 3,
  // Every unit carries a ¾" top and bottom shelf (owner, 2026-07-28).
  sectionShelfThicknessUnits: 0.75,
  // ---- the 32 mm SYSTEM (ADR 0061, owner 2026-08-09) -----------------------
  // "We use the 32MM system so we should stick with that." The owner's exact
  // geometry: a 5 mm hole whose CENTRE is 9.5 mm above the PANEL'S BOTTOM EDGE,
  // repeating every 32 mm; a legal panel height ends 9.5 mm above its top hole.
  //   holeCentreMm(n) = 32n − 22.5      legalPanelHeightMm(n) = 32n − 13
  // An ADJUSTABLE shelf rests on pins with its BOTTOM face at the hole; a FIXED
  // (rafix-bolted) shelf is CENTRED on it.
  //
  // MILLIMETRES are the record. The shop's system is metric even though this
  // UI is imperial, and carrying inches would accumulate rounding up a 76-hole
  // panel. See src/geometry/system-holes.js for the conversion, which happens
  // in exactly one place.
  systemHolePitchMm: 32,
  systemHoleFirstOffsetMm: 9.5,
  // What a DESIGNER sees. Owner, 2026-08-09: "designers won't be able to deal
  // with metric so display imperial rounded to 1/4" (but use actuals for
  // placement so the system is always using actuals to avoid drift)". So every
  // System-32-derived figure on the sketch, the elevation and the interior
  // editor renders as imperial rounded to this step — a PRESENTATION step
  // applied at the last moment. Nothing stored or solved is ever rounded, and
  // millimetres never appear outside Admin, where they are the rule values.
  systemDisplayRoundUnits: 0.25,
  // Whether EDITING a section or space height snaps it to the nearest legal
  // System-32 panel height (32n − 13 mm). On by default — the shop cannot build
  // a panel that ends anywhere else. Deliberately an EDIT-time rule: stored
  // heights are never rewritten on load, so no existing job moves (the app's
  // long-standing 95.25" is 0.014" off the true 95.236" and is not worth
  // migrating anyone's drawing over).
  snapPanelHeightsToSystem: true,
  // A DOUBLE-THICK shelf is this many standard boards thick (owner: "it would
  // be nicer if users could just select a shelf and tag it as double thick and
  // it becomes a single shelf that is 1 1/2" thick"). 2 × ¾" = 1½".
  doubleThickShelfMultiplier: 2,
  // Gap under the BOTTOM shelf of a unit standing on the FLOOR, so base trim
  // passes behind it: nominally "3 inches off the floor", actually 2½" of clear
  // gap (owner). On a platform there is no gap — the bottom shelf sits directly
  // on the platform, at the bottom of the panel.
  floorShelfGapUnits: 2.5,
  // A taller double-hang adds its leftover height as a top opening only when
  // that opening is useful. Below this, no top divider/opening is built and
  // the two hang zones absorb the remainder (owner C3).
  hangingTopOpeningMinUnits: 7,
  // Mirrors the seeded elevation_reference_lines rule (see migrations). Heights
  // are in the room's unit of measure, measured from finished floor.
  // default_visible: what a FRESH sketch shows — only the 96" panel (owner,
  // 2026-07-23; ~95% of rooms are 84"/96" and 96" is primary). Per-sketch
  // designer choices override it in both directions.
  elevationReferenceLines: [
    // Panel lines are drawn at their ACTUAL height, not the nominal name
    // (ADR 0011): an 84" panel is really 83.9", a 96" panel really 95.25".
    // The 3" bottom-shelf line marks where sections start off the floor and is
    // ON by default (owner, 2026-07-28 — every job has it).
    { offset_from_floor_units: 3, kind: 'base_shelf', style: 'dashed', label: '3" bottom shelf', default_visible: true },
    { offset_from_floor_units: 83.9, nominal_units: 84, kind: 'panel_height', style: 'solid', label: '84" panel', default_visible: false },
    { offset_from_floor_units: 95.25, nominal_units: 96, kind: 'panel_height', style: 'solid', label: '96" panel', default_visible: true },
    { offset_from_floor_units: 110, kind: 'stacked_unit', style: 'dashed', label: '110" AFF (stacked)', default_visible: false },
    { offset_from_floor_units: 36, kind: 'work_surface', style: 'dashed', label: '36" countertop', default_visible: false },
    { offset_from_floor_units: 30, kind: 'work_surface', style: 'dashed', label: '30" desk', default_visible: false },
  ],
  /**
   * Mirrors sketch.obstruction_types. Dimensions are in the room's unit of
   * measure; offsetFromFloor is to the BOTTOM edge of the item.
   *
   * Electrical plate sizes come from Leviton's Wallplate Size Guide (Q-1289):
   * 1-gang 2.75 x 4.50, 2-gang 4.56 x 4.50 (2.75 + 1.81 per extra gang).
   * Switch and receptacle plates are the SAME rectangle — only the cut-out
   * differs — which is why they share dimensions here. The plate, not the box,
   * is what shows on an elevation.
   *
   * Mounting heights are trade CONVENTIONS, not code (the NEC sets receptacle
   * spacing, not height): 12" to the bottom of a receptacle box, 48" to the
   * centre of a switch box (a 4.5" plate centred at 48 sits at 45.75).
   *
   * The four window sizes are the Southwest Florida distributor STOCK grid.
   * They are inferred from what distributors warehouse, not measured install
   * frequency — see the migration for the full caveat. Sill heights vary widely
   * and are starting points only.
   */
  obstructionTypes: [
    { key: 'outlet', code: 'O', label: 'Outlet — duplex (1-gang)', width: 2.75, height: 4.5, depth: null, offsetFromFloor: 12 },
    { key: 'outlet_quad', code: 'O', label: 'Outlet — quad (2-gang)', width: 4.56, height: 4.5, depth: null, offsetFromFloor: 12 },
    { key: 'light_switch', code: 'S', label: 'Light switch (1-gang)', width: 2.75, height: 4.5, depth: null, offsetFromFloor: 45.75 },
    { key: 'switch_2gang', code: 'S', label: 'Light switch (2-gang)', width: 4.56, height: 4.5, depth: null, offsetFromFloor: 45.75 },
    // Door and window plan bodies are 6" deep and inset 5¼" from the room-side
    // face. On the standard 4½" wall that leaves ¾" visible on BOTH faces.
    // `inset` is deliberately positive business language; addObstruction()
    // translates it to the renderer's signed faceOffsetUnits coordinate.
    { key: 'window_bath', code: 'W', label: 'Window — 26 × 38⅛ (bath/laundry)', width: 26, height: 38.12, depth: 6, inset: 5.25, offsetFromFloor: 42 },
    { key: 'window', code: 'W', label: 'Window — 36¾ × 50⅜ (bedroom)', width: 36.75, height: 50.37, depth: 6, inset: 5.25, offsetFromFloor: 36 },
    { key: 'window_egress', code: 'W', label: 'Window — 36¾ × 62¾ (bedroom/egress)', width: 36.75, height: 62.75, depth: 6, inset: 5.25, offsetFromFloor: 30 },
    { key: 'window_great', code: 'W', label: 'Window — 52⅞ × 62¾ (great room)', width: 52.87, height: 62.75, depth: 6, inset: 5.25, offsetFromFloor: 30 },
    // Unified wall-attached opening. Its descriptive `opening` payload selects
    // swing/pocket/bifold/bypass/cased/uncased behavior; it never splits the
    // physical wall or loses the header above it.
    { key: 'wall_opening', code: 'OP', label: 'Opening / door', width: 30, height: 80, depth: 6, inset: 5.25, offsetFromFloor: 0 },
    // Doors as OBSTRUCTIONS (owner, 2026-07-29). Distinct from the toolbar's
    // "Add door", which SPLITS the chain into wall/opening/wall for a cased
    // opening with no wall above. A real door has a header: the wall continues
    // over it, keeps one wall number, and uppers can sit above. Sections do not
    // yet know a door blocks the floor in front of it — that is the second half
    // of this item and is not built.
    { key: 'door_30', code: 'D', label: 'Door — 30 × 80', width: 30, height: 80, depth: 6, inset: 5.25, offsetFromFloor: 0 },
    { key: 'door_32', code: 'D', label: 'Door — 32 × 80', width: 32, height: 80, depth: 6, inset: 5.25, offsetFromFloor: 0 },
    { key: 'door_36', code: 'D', label: 'Door — 36 × 80', width: 36, height: 80, depth: 6, inset: 5.25, offsetFromFloor: 0 },
    { key: 'sprinkler', code: 'SP', label: 'Sprinkler', width: 3.25, height: 3.25, depth: 0.5, offsetFromFloor: 108 },
    { key: 'access_panel', code: 'AP', label: 'Access panel — 14 × 14', width: 14, height: 14, depth: null, offsetFromFloor: 24 },
    { key: 'hvac_vent', code: 'V', label: 'HVAC vent', width: 14, height: 6, depth: 4, offsetFromFloor: 6 },
    { key: 'plumbing', code: 'P', label: 'Plumbing', width: 6, height: 6, depth: 2, offsetFromFloor: 18 },
    // Ceiling-plan fixtures use the same admin-maintained type catalog, but
    // `host: ceiling` keeps them out of the wall/elevation palette. Width and
    // depth are their plan footprint; height is the drop below the ceiling.
    { key: 'ceiling_fan', code: 'CF', label: 'Ceiling fan', width: 52, height: 12, depth: 52, offsetFromFloor: 0, host: 'ceiling' },
    { key: 'ceiling_light', code: 'CL', label: 'Ceiling light', width: 6, height: 1, depth: 6, offsetFromFloor: 0, host: 'ceiling' },
    { key: 'ceiling_sprinkler', code: 'SP', label: 'Sprinkler', width: 3.25, height: 0.5, depth: 3.25, offsetFromFloor: 0, host: 'ceiling' },
    // SOFFIT (owner, 2026-07-29; built 2026-08-05 — ADR 0053). "A box at the
    // top of a wall that drops a stated amount below the ceiling and projects a
    // stated depth into the room, blocking the wall(s) it runs into — basically
    // a very large obstruction."
    //   anchor: 'ceiling'   → placed at ceiling − height when added, since a
    //                         drop is what the designer measures, not a height
    //                         off the floor.
    //   capsSectionHeight   → its underside is the ceiling for cabinetry
    //                         beneath it.
    // width/depth are starting points; every soffit is job-specific.
    {
      key: 'soffit',
      code: 'SF',
      label: 'Soffit',
      width: 24,
      height: 12,
      depth: 24,
      offsetFromFloor: 84,
      anchor: 'ceiling',
      capsSectionHeight: true,
    },
    { key: 'custom', code: 'X', label: 'Custom / other', width: 12, height: 12, depth: null, offsetFromFloor: 36 },
  ],
  // ---- CV pricing (ADR 0058, plan §5/4e) -----------------------------------
  // Every price below is DATA (plan §1.4: admin-maintained, no hardcoded
  // prices). The four CV_* blocks are IMPORTED from src/config/cv-pricing-data.js,
  // a file GENERATED from the Designer Portal repo's canonical parser outputs
  // by scripts/import-pricing-rules.mjs — never typed here. Like every other
  // fallback in this file they are the offline seed; business_rules rows
  // override them at runtime.
  /** The canonical CV section catalog priceSection() prices against. */
  cvSectionCatalog: CV_SECTION_CATALOG,
  /** Material-level multiplier curve (M_Lvl calibration rows, plan §1.2).
   *  Applied to a row's materialPrice ONLY — nonMaterialPrice never scales. */
  cvMaterialMultipliers: CV_MATERIAL_MULTIPLIERS,
  /** Countertop pricing: $ per contiguous top + per-sq-ft material rates. */
  cvCountertops: CV_COUNTERTOPS,
  /** Accessories priced today: elite toe stops, lit shelves, the nine hampers. */
  cvAccessories: CV_ACCESSORIES,
  /**
   * The OWNER-MAINTAINED sketch typeCode ↔ CV config mapping (the mapping
   * doc: docs/SECTION_TYPE_MAPPING_AND_BUCKETS_PROPOSAL.md §1 in the Designer
   * Portal repo, owner-answered 2026-08-09). NO GUESSING: a typeCode with no
   * row here is "needs mapping" in the pricing panel, never a fuzzy match.
   *
   * Row fields:
   *   code               sketch typeCode (the tool's Admin grid). Both the
   *                      compiled fallback's codes (SH, HAMP) and the live
   *                      grid's (SV, HMP) are listed where they differ — the
   *                      mapping doc names the live codes, this repo's seed
   *                      presets the others; same meaning, same CV config.
   *   class              CV Section Category ID (T/B/U; ST is applied
   *                      automatically to stacked units via stackedClass).
   *   config             CV Section Type ID to price as.
   *   cvHeightIn         fixed CV height for types whose code carries a
   *                      nominal height (B34S → the B_35 rows, owner-decided),
   *                      overriding the solved height.
   *   interim            true = owner-decided stand-in pricing (LH/MH price
   *                      as DH until CV adds LH/MH rooms) — badged in the UI.
   *   configByShoeStyle  pick FSS/SSS from the composed shoeShelves style.
   *   configByDrawerCount map composed drawer-face count → config.
   *   doorConfig         historical doored-twin metadata retained through
   *                      admin saves; ADR 0106 replaced explicit-door catalog
   *                      deltas with the `doorStyles` area mechanism.
   *   hamperAccessory    true = price the carcass + a hamper accessory range
   *                      (mapping doc: HMP = FS carcass + hamper accessory).
   *   zeroPrice          true = decided $0 (OPEN — a structural spacer; its
   *                      panels still count in the run math).
   */
  cvTypeMapping: {
    rows: [
      { code: 'DH', class: 'T', config: 'DH', doorConfig: 'DHDr' },
      { code: 'LH', class: 'T', config: 'DH', doorConfig: 'DHDr', interim: true },
      { code: 'MH', class: 'T', config: 'DH', doorConfig: 'DHDr', interim: true },
      { code: 'SH', class: 'T', config: 'FS', doorConfig: 'FS-2dr' },
      { code: 'SV', class: 'T', config: 'FS', doorConfig: 'FS-2dr' },
      { code: 'SS', class: 'T', config: 'SSS', configByShoeStyle: true },
      { code: 'DRW', class: 'T', config: 'DRW4', configByDrawerCount: { 3: 'DRW3', 4: 'DRW4', 5: 'DRW5' } },
      { code: 'HMP', class: 'T', config: 'FS', doorConfig: 'FS-2dr', hamperAccessory: true },
      { code: 'HAMP', class: 'T', config: 'FS', doorConfig: 'FS-2dr', hamperAccessory: true },
      { code: 'OPEN', zeroPrice: true },
      { code: 'B34S', class: 'B', config: 'ShOp', cvHeightIn: 35, doorConfig: 'ShDr' },
      { code: 'B34D', class: 'B', config: 'DRW4', cvHeightIn: 35 },
      // The N3 rows (owner, 2026-08-29). Heights pinned to where the
      // catalog actually prices each family — see the presets' comment.
      // B34DD/B18F/B18F3 deliberately carry NO doorConfig: their configs
      // already include the doors/faces, and a delta on top would
      // double-charge (the C1/SD-124 lesson applied at authoring time).
      { code: 'B28F', class: 'B', config: 'FS', cvHeightIn: 28.5, doorConfig: 'FS-2dr' },
      { code: 'B28D', class: 'B', config: 'DRW3', cvHeightIn: 28.5, configByDrawerCount: { 3: 'DRW3', 4: 'DRW4' } },
      { code: 'B34DD', class: 'B', config: 'DRW-2dr', cvHeightIn: 35 },
      { code: 'B18F', class: 'B', config: 'DRW-2F', cvHeightIn: 28.5 },
      { code: 'B18F3', class: 'B', config: 'DRW3-2F', cvHeightIn: 35 },
      { code: 'ESS84', class: 'T', config: 'ESS', cvHeightIn: 84 },
      { code: 'U36', class: 'U', config: 'ShOp', cvHeightIn: 36, doorConfig: 'ShDr' },
      { code: 'LB28S', class: 'B', config: 'Lsh', cvHeightIn: 28.5 },
      { code: 'LB34S', class: 'B', config: 'Lsh', cvHeightIn: 35 },
      { code: 'LT95', class: 'T', config: 'Lsh', cvHeightIn: 95 },
      { code: 'LU24', class: 'U', config: 'Lsh', cvHeightIn: 24 },
      { code: 'LU36', class: 'U', config: 'Lsh', cvHeightIn: 36 },
      { code: 'LU42', class: 'U', config: 'Lsh', cvHeightIn: 42 },
      // Wall beds (D95). No `cvHeightIn` — see the presets' comment: class WB
      // is exact-only, so pinning the height would price a resized bed at the
      // catalog figure instead of reporting that it cannot be quoted.
      // Angled shelves (D101). `T|AngSh` had NO mapping row at all — one of
      // the eight catalog families the RS-7 sweep found unreachable for that
      // reason. Pinned to the nominal 95 exactly as LT95 is.
      { code: 'AT95', class: 'T', config: 'AngSh', cvHeightIn: 95 },
      { code: 'WBTWH', class: 'WB', config: 'Bd TwH' },
      { code: 'WBQNV', class: 'WB', config: 'BdQnV' },
    ],
    /**
     * The sketch's ACTUAL panel heights ↔ CV's NOMINAL height codes (ADR
     * 0011: a "96-inch" panel is really 95.25", an "84" really 83.9"; the CV
     * catalog keys rows by the nominal figure). Without this identity every
     * default-height section would badge as a nearest-height fallback for
     * what is physically the exact catalog panel. Exact-match only — any
     * other height flows through untouched and takes priceSection's own
     * nearest handling, badged.
     */
    heightAliases: [
      { actualUnits: 95.25, cvHeightIn: 95 },
      { actualUnits: 83.9, cvHeightIn: 84 },
    ],
    /** Class applied to STACKED units (mapping doc: ST sections map by
     *  stacked-unit height; price rows exist only at 12"d today, so other
     *  depths take the badged nearest-depth fallback). */
    stackedClass: 'ST',
    /** Expected composed drawer faces per DRW-family config, for the
     *  drawer-count-difference delta (plan §5/4e). */
    configDrawerCounts: { DRW3: 3, DRW4: 4, DRW5: 5 },
    /** Config pairs whose price difference IS the per-drawer delta: the
     *  general one, and the one used above a DRW5 anchor. */
    perDrawerDeltaPair: ['DRW4', 'DRW3'],
    extraDrawerDeltaPair: ['DRW5', 'DRW4'],
  },
  // ---- Basic mode buckets (ADR 0059, the room-sketch UI plan) ---------------
  /**
   * BUCKET definitions for Basic mode (the portal repo's
   * docs/ROOM_SKETCH_UI_PLAN.md + docs/bucket-span-preview.md — seeded from
   * the preview's core table, 2026-08-09). A bucket is a broad card ("hanging
   * section") that prices as a SPAN across the cv_section_catalog rows it
   * covers: low = the cheapest qualifying row, high = the most expensive —
   * deliberately DIFFERENT concrete interpretations of the same count (the
   * owner's example: low six 84×12 open sections, high six 95×16 with doors).
   * Door-config rows (DHDr, FS-2dr, ShDr) are IN the qualifying set, which is
   * how "the high has doors" happens without a separate doors input.
   *
   * Fields (all DATA — admin-editable, cleanup expected post-launch):
   *   key/label           identity + card text.
   *   classes/configs     which catalog rows qualify (class + config filter).
   *   boundsMode          'space' = the designer's per-space bounds narrow it;
   *                       'own'   = the fixed `bounds` below (base/upper live
   *                                 on their own height ladder, untouched by
   *                                 the tall bounds);
   *                       'all'   = unbounded (span doc: stacked matches the
   *                                 unit it sits on, wall beds and L-shelves
   *                                 span their own few rows).
   *   depthFloorIn        minimum depth regardless of the designer's lower
   *                       bound (drawer boxes need 16" — span doc §rules).
   *   representativeType  the sketch typeCode a "place" action stamps
   *                       (ADR 0059 conversion); null = no sketch type exists
   *                       yet, the card prices but cannot be placed.
   */
  basicBuckets: [
    { key: 'hanging', label: 'Hanging section', classes: ['T'], configs: ['DH', 'DHDr'], boundsMode: 'space', representativeType: 'DH' },
    { key: 'shelving', label: 'Shelving section', classes: ['T'], configs: ['FS', 'FS-2dr'], boundsMode: 'space', representativeType: 'SH' },
    { key: 'drawer', label: 'Drawer section', classes: ['T'], configs: ['DRW4', 'DRW5'], boundsMode: 'space', depthFloorIn: 16, representativeType: 'DRW' },
    { key: 'shoe', label: 'Shoe shelves', classes: ['T'], configs: ['FSS', 'SSS'], boundsMode: 'space', representativeType: 'SS' },
    { key: 'corner', label: 'Corner / L-shelves', classes: ['B', 'T', 'U'], configs: ['Lsh'], boundsMode: 'all', representativeType: 'LT95' },
    { key: 'angled', label: 'Angled shelves', classes: ['T'], configs: ['AngSh'], boundsMode: 'space', representativeType: 'AT95' },
    { key: 'base', label: 'Base cabinet', classes: ['B'], configs: ['FS', 'FS-2dr', 'ShOp', 'DRW-2dr', 'DRW3', 'DRW4', 'DRW-2F', 'DRW3-2F'],
      boundsMode: 'own', bounds: { lowHeightIn: 28.5, lowDepthIn: 16, highHeightIn: 35, highDepthIn: 24 }, representativeType: 'B34S' },
    { key: 'upper', label: 'Upper cabinet', classes: ['U'], configs: ['ShOp', 'ShDr', 'Lsh'],
      boundsMode: 'own', bounds: { lowHeightIn: 24, lowDepthIn: 12, highHeightIn: 42, highDepthIn: 16 }, representativeType: 'U36' },
    { key: 'stacked', label: 'Stacked unit', classes: ['ST'], configs: ['ShOp', 'ShDr'], boundsMode: 'all', representativeType: null },
    // `representativeType` is singular and this bucket spans TWO units that
    // differ in size and orientation — twin horizontal (80w × 50h) and queen
    // vertical (64w × 87.7h). The precedent is that a bucket names a starting
    // point and the designer changes type after placing: `base` names one of
    // eight configs, `corner` one of fourteen rows. The QUEEN is the OWNER'S
    // RULING (2026-08-27, ADR 0099 amendment) — the provisional twin pick and
    // its lower-fits-under-more reasoning are recorded there; the twin stays
    // one type-swap away. (This comment said PROVISIONAL for two days after
    // the ruling landed on the next line, and a register sweep resurrected
    // the question from it — corrected 2026-08-29.)
    { key: 'wallbed', label: 'Wall bed', classes: ['WB'], configs: ['Bd TwH', 'BdQnV'], boundsMode: 'all', representativeType: 'WBQNV' },
  ],
  /**
   * Which buckets each SPACE TYPE offers (the span doc's PROPOSED availability
   * matrix, seeded 2026-08-09 — owner cleanup expected post-launch, never a
   * code change). Keyed bucket → space types, mirroring the doc's rows.
   */
  basicBucketAvailability: {
    hanging: ['closet', 'garage', 'laundry'],
    shelving: ['closet', 'office', 'wallbed', 'pantry', 'garage', 'laundry'],
    drawer: ['closet', 'office', 'pantry', 'garage'],
    shoe: ['closet'],
    // Owner, 2026-08-09: "L-shelves is a necessary section to include in pantry
    // space as a large number of pantries contain l-shelves."
    corner: ['closet', 'office', 'garage', 'pantry'],
    angled: ['closet'],
    base: ['office', 'wallbed', 'pantry', 'garage', 'laundry'],
    upper: ['office', 'pantry', 'laundry'],
    stacked: ['closet', 'pantry', 'garage'],
    wallbed: ['office', 'wallbed'],
  },
  /** The space types the availability matrix speaks (first = default). */
  basicSpaceTypes: [
    { key: 'closet', label: 'Closet' },
    { key: 'office', label: 'Office' },
    { key: 'wallbed', label: 'Wall bed' },
    { key: 'pantry', label: 'Pantry' },
    { key: 'garage', label: 'Garage' },
    { key: 'laundry', label: 'Laundry' },
  ],
  /**
   * Default designer bounds for 'space'-bounded buckets: the tall ladder,
   * 84"h × 12"d up to 95"h × 16"d (span doc §rules). The designer widens or
   * narrows them per space; out-of-bounds rows simply stop qualifying.
   */
  basicDefaultBounds: { lowHeightIn: 84, lowDepthIn: 12, highHeightIn: 95, highDepthIn: 16 },
  /**
   * Quick Ballpark quick-mode core-section ids → bucket keys, for importing a
   * quick estimate as a staged tray (UI plan §Adoption path; the transport
   * itself is a follow-up — quickEstimateToBuckets() is the pure half).
   * `connectingShelves` is deliberately UNMAPPED: no bucket exists for the
   * corner-bridge concept, and inventing one would be a guess — the import
   * surfaces it as an unmapped line instead.
   */
  quickCoreSectionBucketMap: {
    hanging: 'hanging',
    shelving: 'shelving',
    lShelves: 'corner',
    baseCabinets: 'base',
    uppers: 'upper',
  },
  /**
   * Garage platform charge, $ per (non-OPEN) run section when the space's
   * platform toggle (ADR 0040) is non-Floor. Owner decision (plan §1.3):
   * a garage is an existing section + platform, $250 flat per section —
   * rounded by the owner from the measured $251 delta. Data, not a constant.
   */
  garagePlatformChargePerSection: 250,
  // CROWN and BASE TRIM, priced per LINEAR FOOT (owner, 2026-08-10: "It should
  // be based on linear ft. — put that as a maintenance table and us $15 as the
  // current default"). Two rules rather than one so they can diverge without a
  // code change; both start at the owner's $15.
  //
  // Base trim was already DRAWN — the dashed 3" bottom-shelf reference line —
  // but never priced (owner: "It may show up in the rendering, but it is the
  // cost that is needed"). Crown was neither drawn nor priced.
  //
  // Base trim is NOT an accessory (owner: "It was not shown as an accessory,
  // it is just toggled on/off"), so it is its own rule and its own line, and
  // it follows the section's BOTTOM SHELF: no bottom shelf, no base trim.
  crownPricePerLinearFoot: 15,
  baseTrimPricePerLinearFoot: 15,
  /**
   * The ballpark low/high spread applied to every priced figure (the ballpark
   * convention: low = 100%, high = 125%).
   */
  pricingRangeLowPercent: 100,
  pricingRangeHighPercent: 125,
  /**
   * BASIC-mode DRAWER ADDER — dollars per drawer, on a staged bucket card
   * (owner, verbatim): "For basic, the low end of the range for an adder
   * should be $225 and the high end $325 (assumes deco drawer faces). This way
   * we don't need to worry about do I have a 1,2,3,4,5,6 drawer section with
   * pricing."
   *
   * That is the whole point of the adder: Basic never asks the catalog for a
   * DRW4-vs-DRW5 row, it charges a flat per-drawer band on top of whatever
   * section the card already spans.
   *
   * ⚠ These two are the FINAL low and high figures. `pricingRangeLowPercent` /
   * `pricingRangeHighPercent` are deliberately NOT applied on top of them — the
   * ballpark spread the owner quoted is already the distance between $225 and
   * $325, and multiplying again would double-count it. A test asserts the
   * adder is untouched by the spread.
   */
  basicDrawerAdderPriceLow: 225,
  basicDrawerAdderPriceHigh: 325,
});

/**
 * Maps a row set from `sketch.business_rules` (rule_key -> rule_value) onto the
 * shape the app uses, falling back to the defaults for any missing key.
 *
 * The sync engine will call this with whatever it pulled; the app never has to
 * know whether a given rule was live or defaulted.
 *
 * @param {Record<string, unknown>} [rows] rule_key -> parsed rule_value
 * @returns {BusinessRules}
 */
/**
 * A section type's DEFAULT interior composition (ADR 0054, plan §5/4a).
 *
 * The preset's `interior` template wins when it carries one. A rule set whose
 * presets predate templates — the LIVE section_type_presets row until it is
 * re-saved or migrated — derives the same composition from the legacy
 * flags/counts, faithfully:
 *   - hanging  → hangRods × rod, category from the type's hangCategory
 *     (stamped onto each ROD element — the category lives on the element,
 *     groundwork for ADR 0051 step 2)
 *   - shoes    → one shoeShelves (FSS) of defaultShelves
 *   - shelving (non-shoes) → defaultShelves × (shelf + openSpace)
 *   - drawers  → one drawerBank of defaultDrawers (a matching drawerBankPresets
 *     bank by count, else defaultDrawerHeightUnits faces) at the BOTTOM of the
 *     band, under openSpace — CV elevations put drawer banks at the floor end
 *   - none of the above → openSpace
 *
 * Count OVERRIDES let a legacy SECTION's own shelfCount/drawerCount replace
 * the preset's defaults, so a designer's edited counts survive the derivation.
 * Returns a fresh normalized array every call — never a shared template.
 *
 * @param {object} preset a sectionTypePresets entry
 * @param {BusinessRules} rules resolved rules (drawer banks + default height)
 * @param {{shelfCount?:number|null, drawerCount?:number|null}} [counts]
 * @returns {Array<object>} normalized interior elements, top → bottom
 */
export function defaultInteriorForPreset(preset = {}, rules = DEFAULT_BUSINESS_RULES, counts = {}) {
  const flags = Array.isArray(preset.flags) ? preset.flags : [];
  const shelfCount = Number.isInteger(counts.shelfCount) && counts.shelfCount >= 0
    ? counts.shelfCount
    : (Number.isInteger(preset.defaultShelves) && preset.defaultShelves >= 0 ? preset.defaultShelves : 0);
  const drawerCount = Number.isInteger(counts.drawerCount) && counts.drawerCount >= 0
    ? counts.drawerCount
    : (Number.isInteger(preset.defaultDrawers) && preset.defaultDrawers >= 0 ? preset.defaultDrawers : 0);

  const template = normalizeInterior(preset.interior);
  if (template) {
    // The template is the base; the section's own counts still win over the
    // preset defaults (a designer who set 9 shoe shelves keeps 9).
    return ensureInteriorStructure(applyCountsToInterior(template, {
      shelfCount: flags.includes('shelving') || flags.includes('shoes') ? shelfCount : undefined,
      drawerCount: flags.includes('drawers') ? drawerCount : undefined,
    }, rules));
  }

  const out = [];
  if (flags.includes('hanging')) {
    const rods = Number.isInteger(preset.hangRods) && preset.hangRods > 0 ? preset.hangRods : 1;
    const hangCategory = typeof preset.hangCategory === 'string' && preset.hangCategory ? preset.hangCategory : null;
    if (rods === 2) {
      out.push(
        { kind: 'openSpace', autoHangRole: 'top-opening' },
        { kind: 'shelf', fixed: true, autoHangRole: 'top-divider' },
        { kind: 'rod', hangCategory, autoHangRole: 'upper-rod' },
        { kind: 'shelf', fixed: true, autoHangRole: 'middle-divider' },
        { kind: 'rod', hangCategory, autoHangRole: 'lower-rod' },
      );
    } else {
      for (let i = 0; i < rods; i += 1) out.push({ kind: 'rod', hangCategory });
    }
  }
  if (flags.includes('shoes')) {
    out.push({ kind: 'shoeShelves', style: 'FSS', count: shelfCount });
  } else if (flags.includes('shelving')) {
    // Shelves sit BETWEEN spaces (owner, 2026-08-29: shelving SECTIONS are
    // the designer's unit — "the number of actual shelves would fall out of
    // that"). Leading with a space keeps a shelf off the band boundary,
    // where it would duplicate the structural board beside it; N shelves
    // separate N+1 sections.
    out.push({ kind: 'openSpace' });
    for (let i = 0; i < shelfCount; i += 1) out.push({ kind: 'shelf', fixed: false }, { kind: 'openSpace' });
  }
  if (flags.includes('drawers') && drawerCount > 0) {
    out.push({ kind: 'openSpace' }, { kind: 'drawerBank', drawers: drawerFaces(drawerCount, rules) });
  }
  if (out.length === 0) out.push({ kind: 'openSpace' });
  // Every rod gets its structural support shelf and every drawer bank its
  // fixed shelf above and below, on BOTH derivation paths and in the editor —
  // one helper, so they cannot diverge (ADR 0060 item 4, ADR 0062).
  return ensureInteriorStructure(normalizeInterior(out));
}

/**
 * The solver options every interior solve in the app shares, mapped from the
 * resolved rules. ONE function, so the wall elevation, the measurement sheet
 * and the editor's live preview can never solve with different assumptions —
 * the same reason elevationRenderOpts() exists (ADR 0060).
 *
 * @param {BusinessRules} rules
 * @returns {object} opts for solveInterior()
 */
export function interiorSolveOpts(rules = DEFAULT_BUSINESS_RULES, { requiredHolesOnly = false, panelBottomUnits } = {}) {
  const shelfT = rules.sectionShelfThicknessUnits > 0 ? rules.sectionShelfThicknessUnits : 0.75;
  const displayedReferencePanel = (rules.elevationReferenceLines ?? [])
    .find((line) => line?.kind === 'panel_height' && line?.nominal_units === 84)?.offset_from_floor_units ?? 83.9;
  const referencePanel = nearestLegalPanelHeight(displayedReferencePanel, {
    pitchMm: rules.systemHolePitchMm,
    firstOffsetMm: rules.systemHoleFirstOffsetMm,
  })?.heightUnits ?? displayedReferencePanel;
  return {
    shelfThicknessUnits: shelfT,
    // The rod's drop is a HOLE COUNT (ADR 0062 §7), derived against the pitch.
    rodSupportShelfHoleOffset: rules.rodSupportShelfHoleOffset,
    // The 32 mm system (ADR 0061). `requiredHolesOnly` is the SECTION's flag
    // and `panelBottomUnits` is the placed UNIT's datum — neither is a rule, but
    // both ride in here so every solve site (wall elevation, measurement sheet,
    // editor preview) states them the same way or not at all.
    systemHolePitchMm: rules.systemHolePitchMm,
    systemHoleFirstOffsetMm: rules.systemHoleFirstOffsetMm,
    doubleThickMultiplier: rules.doubleThickShelfMultiplier,
    requiredHolesOnly: requiredHolesOnly === true,
    panelBottomUnits,
    standardDoubleHangBandUnits: Math.max(0,
      referencePanel - (rules.floorShelfGapUnits > 0 ? rules.floorShelfGapUnits : 0) - shelfT * 2),
    autoTopOpeningMinUnits: rules.hangingTopOpeningMinUnits > 0 ? rules.hangingTopOpeningMinUnits : 7,
    drawerToeHeightUnits: rules.drawerToeHeightUnits,
    drawerOverlayUnits: rules.drawerOverlayUnits,
    drawerVerticalSpacingUnits: rules.drawerVerticalSpacingUnits,
    drawerStackMaxHeightUnits: rules.drawerStackMaxHeightUnits,
  };
}

/** Drawer faces for a stated count: a preset bank of that exact size, else
 *  count × the default face height. Data-driven, never a guess. */
function drawerFaces(count, rules = DEFAULT_BUSINESS_RULES) {
  const banks = Array.isArray(rules.drawerBankPresets) ? rules.drawerBankPresets : [];
  const match = banks.find((b) => Array.isArray(b.heights) && b.heights.length === count);
  if (match) return match.heights.map((h) => ({ heightUnits: h }));
  const face = rules.defaultDrawerHeightUnits > 0 ? rules.defaultDrawerHeightUnits : 6.25;
  return Array.from({ length: count }, () => ({ heightUnits: face }));
}

/**
 * Re-applies shelf/drawer COUNTS onto an existing interior — the one edit the
 * legacy Shv/Drw count fields can still express (ADR 0054: the interior is the
 * source of truth, so a count edit must flow INTO it, not around it).
 *
 *   - drawerCount resizes the FIRST drawer bank: shrink drops from the bottom,
 *     grow repeats the bank's last face (else the default face height). A
 *     count with no bank yet adds one at the top.
 *   - shelfCount sets the FIRST shoeShelves count when one exists (shoe
 *     sections state shelves, ADR 0039); otherwise it adds/removes plain
 *     shelf elements (removals from the BOTTOM of the list, additions with
 *     their openSpace, matching the SH template's rhythm).
 *
 * Returns a new normalized array; null/undefined counts leave that axis alone.
 */
export function applyCountsToInterior(interior, { shelfCount, drawerCount } = {}, rules = DEFAULT_BUSINESS_RULES) {
  let out = (normalizeInterior(interior) ?? []).map((el) => ({ ...el }));

  if (Number.isInteger(drawerCount) && drawerCount >= 0) {
    const bank = out.find((el) => el.kind === 'drawerBank');
    if (bank) {
      const drawers = bank.drawers.map((d) => ({ ...d }));
      const last = drawers[drawers.length - 1];
      const face = last?.heightUnits
        ?? (rules.defaultDrawerHeightUnits > 0 ? rules.defaultDrawerHeightUnits : 6.25);
      while (drawers.length > drawerCount) drawers.pop();
      while (drawers.length < drawerCount) drawers.push({ heightUnits: face });
      bank.drawers = drawers;
    } else if (drawerCount > 0) {
      // Drawer banks sit at the BOTTOM of the band (CV convention).
      out.push({ kind: 'drawerBank', drawers: drawerFaces(drawerCount, rules), accessories: [] });
    }
  }

  if (Number.isInteger(shelfCount) && shelfCount >= 0) {
    const shoe = out.find((el) => el.kind === 'shoeShelves');
    if (shoe) {
      shoe.count = shelfCount;
    } else {
      let have = out.filter((el) => el.kind === 'shelf').length;
      for (let i = out.length - 1; i >= 0 && have > shelfCount; i -= 1) {
        if (out[i].kind !== 'shelf') continue;
        const spaceAfter = out[i + 1]?.kind === 'openSpace' ? 1 : 0;
        out.splice(i, 1 + spaceAfter);
        have -= 1;
      }
      while (have < shelfCount) {
        // A shelf never lands against whatever is above it — a separating
        // space goes first when the list does not already end in one, so
        // the composed run always reads space, shelf, space… (owner,
        // 2026-08-29: shelving sections; a boundary shelf duplicates the
        // structural board it touches).
        if (out[out.length - 1]?.kind !== 'openSpace') out.push({ kind: 'openSpace', accessories: [] });
        out.push({ kind: 'shelf', fixed: false, accessories: [] }, { kind: 'openSpace', accessories: [] });
        have += 1;
      }
    }
  }

  return normalizeInterior(out);
}

/**
 * ADR 0051 STEP 2's load shim (executed in ADR 0057, plan §5/4d): re-keys a
 * stored `storageNeeds` object from the legacy per-TYPE hang keys
 * (`hang:DH`, `hang:LH`, `hang:MH`) to the per-CATEGORY keys
 * (`hang:short`, `hang:long`, `hang:medium`) the storage summary and the
 * needs panel now speak. Applied at hydrate — the stored document is only
 * rewritten on the next save, the same policy as every other load migration.
 *
 * The mapping, exactly:
 *   - `hang:<X>` where `<X>` is already a `hang_categories` key → untouched.
 *   - `hang:<X>` where `<X>` (case-insensitively) names a section-type preset
 *     whose `hangCategory` is a valid category → re-keyed to
 *     `hang:<category>`. Figures landing on the same category MERGE:
 *       - `existing` per slot: the sum of the stated figures, null when every
 *         contributor left the slot unstated.
 *       - `needed` per slot: null when NO contributor stated one (the
 *         "same as existing" fallback stays a fallback); otherwise the sum of
 *         each contributor's RESOLVED need (`needed ?? existing ?? 0`) — so a
 *         DH entry riding its fallback still counts its inches when merged
 *         with an LH-turned-short entry that stated a need. This preserves
 *         `resolvedStorageNeed()` totals in both compare modes.
 *   - anything else (unknown type, uncategorized type, shoes/drawers keys) →
 *     preserved untouched, never dropped. NO substitution between categories
 *     (owner decision, ADR 0051) — this merges same-category figures only.
 *
 * @param {Record<string, {existing:object, needed:object}>} needs
 * @param {BusinessRules} rules
 * @returns {Record<string, {existing:object, needed:object}>} a new object
 */
/** The sentinel the countertop picker uses for a thickness no material carries. */
export const COUNTERTOP_OTHER = '__other__';

/**
 * Which countertop choice a STORED thickness reads as.
 *
 * The picker is a material list, but the sketch stores a NUMBER — the two have
 * to agree in both directions, and the interesting direction is opening an old
 * sketch. Three cases, and only three:
 *
 *   - no countertop (0 or absent) → `''`, the "None" choice;
 *   - a thickness a material carries → that material's key;
 *   - any other positive thickness → `COUNTERTOP_OTHER`, with the figure kept.
 *
 * The last case is the one worth naming: a sketch saved when this field was
 * free text can hold 1", which no material in the list has. It shows as Other
 * with 1" still in the box — never rounded to the nearest material, and never
 * silently cleared, because both would change a quoted dimension behind the
 * designer's back.
 *
 * @param {number} thicknessUnits
 * @param {BusinessRules} [rules]
 * @returns {string} the picker's value
 */
export function countertopChoiceForThickness(thicknessUnits, rules = DEFAULT_BUSINESS_RULES) {
  if (!Number.isFinite(thicknessUnits) || thicknessUnits <= 0) return '';
  const presets = Array.isArray(rules?.countertopMaterialPresets) ? rules.countertopMaterialPresets : [];
  const match = presets.find((preset) => preset?.thicknessUnits === thicknessUnits);
  return match ? String(match.key) : COUNTERTOP_OTHER;
}

export function migrateStorageNeedKeys(needs, rules = DEFAULT_BUSINESS_RULES) {
  if (!needs || typeof needs !== 'object') return {};
  const categories = new Set((Array.isArray(rules.hangCategories) ? rules.hangCategories : [])
    .map((c) => String(c?.key ?? '')).filter(Boolean));
  const typeToCategory = new Map();
  for (const p of Array.isArray(rules.sectionTypePresets) ? rules.sectionTypePresets : []) {
    const code = String(p?.code ?? '').toUpperCase();
    const cat = typeof p?.hangCategory === 'string' ? p.hangCategory : null;
    if (code && cat && categories.has(cat)) typeToCategory.set(code, cat);
  }

  const slots = ['space', 'a', 'b'];
  const out = {};
  const mergeInto = (key, entry) => {
    const prior = out[key];
    if (!prior) {
      out[key] = {
        existing: { ...entry.existing }, needed: { ...entry.needed },
        ...(Array.isArray(entry.shoeGroups) ? { shoeGroups: entry.shoeGroups.map((group) => ({ ...group })) } : {}),
      };
      return;
    }
    // Free-form footwear entries are a LIST, not a figure: two rows collapsing
    // onto one row keep both lists. Without this the second contributor's
    // entries were dropped while its summed pair counts survived, which reads
    // as "the app ate my boots row" (see the shoes re-key below).
    if (Array.isArray(entry.shoeGroups) && entry.shoeGroups.length) {
      prior.shoeGroups = [...(prior.shoeGroups ?? []), ...entry.shoeGroups.map((group) => ({ ...group }))];
    }
    for (const slot of slots) {
      const a = prior.existing?.[slot] ?? null;
      const b = entry.existing?.[slot] ?? null;
      const na = prior.needed?.[slot] ?? null;
      const nb = entry.needed?.[slot] ?? null;
      // needed: resolve each side's fallback BEFORE summing, so the merged
      // figure equals the sum of what each entry resolved to on its own.
      const merged = na === null && nb === null
        ? null
        : (na ?? a ?? 0) + (nb ?? b ?? 0);
      prior.existing[slot] = a === null && b === null ? null : (a ?? 0) + (b ?? 0);
      prior.needed[slot] = merged;
    }
  };

  for (const [key, entry] of Object.entries(needs)) {
    if (!entry || typeof entry !== 'object') continue;
    const m = /^hang:(.+)$/.exec(key);
    if (m && !categories.has(m[1])) {
      const cat = typeToCategory.get(m[1].toUpperCase());
      if (cat) { mergeInto(`hang:${cat}`, entry); continue; }
    }
    // Footwear is ONE row now (`shoes:ALL`) rather than one per shoes-flagged
    // type — see storageNeedTypes(). Docs saved under the per-type keys
    // (`shoes:SS`, `shoes:ESS84`) re-key here and MERGE, exactly as the hang
    // re-key does, so a client's typed footwear entries are not silently
    // orphaned by the collapse.
    if (/^shoes:/.test(key) && key !== 'shoes:ALL') { mergeInto('shoes:ALL', entry); continue; }
    mergeInto(key, entry); // already category-keyed, non-hang, or unknown: as-is
  }
  return out;
}

/**
 * The NUMERIC rules the admin maintenance table exposes (owner, 2026-08-05:
 * "these need to be added to the admin maintenance tables").
 *
 * Declared here, beside the defaults, so adding a tunable number is one entry
 * in one place rather than a rule, a resolver line and a form field that can
 * drift apart. `key` is the `business_rules.rule_key`; `prop` is the resolved
 * property the app reads.
 *
 * Deliberately NUMBERS only. The structural rules — the section-type list, the
 * obstruction types, hang categories, reference lines, the Wall 1 selection
 * strategy — are lists and strategies, not quantities: some have their own
 * editor already, and the rest would need real validation before a text box is
 * safe. A wrong number here is visible and reversible; a malformed list is not.
 *
 * `kind` decides how a value is parsed and shown: 'length' accepts what every
 * other length field accepts (30 1/2, 8'6"), 'count' is a whole number,
 * 'degrees' and 'plain' are bare numbers.
 */
/**
 * A hang category's STANDARD hang length, derived from its hole count and the
 * live pitch rule (owner 2026-08-11; see `hangCategories`). Null for a category
 * that states no count — which is what a rule row saved before this existed
 * looks like, so an old stored `hang_categories` simply has no ladder and
 * classification falls back to the section's tag.
 */
export function hangCategoryLengthUnits(categoryKey, rules = DEFAULT_BUSINESS_RULES) {
  const cat = (Array.isArray(rules.hangCategories) ? rules.hangCategories : [])
    .find((c) => c?.key === categoryKey);
  if (!(cat?.holePitches > 0)) return null;
  const pitchMm = rules.systemHolePitchMm > 0 ? rules.systemHolePitchMm : 32;
  return mmToUnits(cat.holePitches * pitchMm);
}

/**
 * The category a MEASURED hang length belongs to — nearest standard wins.
 *
 * ⚠ This is a classification of the RESULT, never a constraint on it. The owner
 * was explicit: a designer with a 56" opening "and add a rod (so ~54" hang
 * height)… they are not saying 'make this a long hang'". So nothing here forces
 * an opening to a standard; it only answers which rung a real measurement is
 * nearest, which is what the storage summary needs in order to stop reporting
 * a stretched rod under whatever its section TYPE was tagged.
 *
 * @returns {string|null} the category key, or null when no ladder is defined
 */
export function classifyHangLength(units, rules = DEFAULT_BUSINESS_RULES) {
  if (!Number.isFinite(units) || units <= 0) return null;
  let best = null;
  let bestGap = Infinity;
  for (const cat of Array.isArray(rules.hangCategories) ? rules.hangCategories : []) {
    const standard = hangCategoryLengthUnits(cat?.key, rules);
    if (standard === null) continue;
    const gap = Math.abs(units - standard);
    if (gap < bestGap) { bestGap = gap; best = cat.key; }
  }
  return best;
}

export const EDITABLE_NUMBER_RULES = Object.freeze([
  { key: 'default_longest_wall_units', prop: 'defaultLongestWallUnits', kind: 'length',
    label: 'Default longest wall', hint: 'A fresh drawing fixes its longest wall at this initial measured length; later readings do not rescale the plan.' },
  { key: 'default_ceiling_height_units', prop: 'defaultCeilingHeightUnits', kind: 'length',
    label: 'Default ceiling height', hint: 'Ceiling height a new sketch starts with.' },
  { key: 'closure_tolerance_units', prop: 'closureToleranceUnits', kind: 'length',
    label: 'Closure tolerance', hint: 'How close the drawn ends must be for a chain to count as a closed room.' },
  { key: 'default_wall_thickness_units', prop: 'defaultWallThicknessUnits', kind: 'length',
    label: 'Wall thickness', hint: 'Drawn wall thickness when a wall has no override.' },
  { key: 'default_door_width_units', prop: 'defaultDoorWidthUnits', kind: 'length',
    label: 'Door width', hint: 'Width of a door opening added from the toolbar.' },
  { key: 'default_door_height_units', prop: 'defaultDoorHeightUnits', kind: 'length',
    label: 'Door height', hint: 'Height a new sketch assumes for doors.' },
  { key: 'door_swing_leaf_allowance_units', prop: 'doorSwingLeafAllowanceUnits', kind: 'length',
    label: 'Door swing allowance', hint: 'Amount added to the measured clear opening when estimating how far a swing door leaf projects into or out of the room.' },
  { key: 'bifold_folded_projection_units', prop: 'bifoldFoldedProjectionUnits', kind: 'length',
    label: 'Bifold folded projection', hint: 'Space a folded bifold stack occupies outside the room: once for a single bifold and at both sides for a double.' },
  { key: 'default_partition_length_units', prop: 'defaultPartitionLengthUnits', kind: 'length',
    label: 'Partition length', hint: 'How far a new partition sticks into the room.' },
  { key: 'attic_ladder_ceiling_inset_units', prop: 'atticLadderCeilingInsetUnits', kind: 'length',
    label: 'Attic ladder ceiling inset', hint: 'How far inside the opening, from its length-anchor wall side, the drop-down ladder meets the ceiling.' },
  { key: 'attic_ladder_side_clearance_units', prop: 'atticLadderSideClearanceUnits', kind: 'length',
    label: 'Attic stair side clearance', hint: 'Clearance inside each side of the attic opening when drawing the stair-down footprint.' },
  { key: 'section_end_reveal_units', prop: 'sectionEndRevealUnits', kind: 'length',
    label: 'End reveal', hint: 'Scribe gap kept between the outermost panel and the wall beside it.' },
  { key: 'section_panel_thickness_units', prop: 'sectionPanelThicknessUnits', kind: 'length',
    label: 'Panel thickness', hint: 'Vertical panel thickness; neighbouring sections share one by default.' },
  { key: 'section_min_opening_units', prop: 'sectionMinOpeningUnits', kind: 'length',
    label: 'Min opening', hint: 'An auto section squeezed below this is flagged. A type may override it.' },
  { key: 'section_max_opening_units', prop: 'sectionMaxOpeningUnits', kind: 'length',
    label: 'Max opening', hint: 'Auto sections never fill wider than this without an explicit override.' },
  { key: 'section_default_depth_units', prop: 'sectionDefaultDepthUnits', kind: 'length',
    label: 'Section depth', hint: 'Depth used when a section and its type state none.' },
  { key: 'section_default_height_units', prop: 'sectionDefaultHeightUnits', kind: 'length',
    label: 'Section height', hint: 'Height used when a section, the space and its type state none.' },
  { key: 'section_hang_clearance_units', prop: 'sectionHangClearanceUnits', kind: 'length',
    label: 'Hang clearance', hint: 'Clear space a hanging section needs in front of it at a corner.' },
  { key: 'section_corner_gap_units', prop: 'sectionCornerGapUnits', kind: 'length',
    label: 'Corner gap (no hanging)', hint: 'Open space left at a corner where NEITHER unit hangs, measured beyond the corner unit’s depth — so it is also the width of the connecting shelf that bridges the corner. Hanging corners use Hang clearance instead.' },
  { key: 'plan_open_span_label_min_units', prop: 'planOpenSpanLabelMinUnits', kind: 'length',
    label: 'Open-span label minimum', hint: 'Open wall spans at least this long show their size on the plan; shorter slack stays unlabelled.' },
  { key: 'abutting_panel_reach_units', prop: 'abuttingPanelReachUnits', kind: 'length',
    label: 'Abutting panel reach', hint: 'Clear space allowed between a wall and the OUTSIDE of the neighbouring run’s end panel for that panel to still be outlined on the elevation. Panel thickness is accounted for automatically.' },
  { key: 'storage_rounding_units', prop: 'storageRoundingUnits', kind: 'length',
    label: 'Storage rounding', hint: 'Linear-inch figures in the storage summary round to this. 0 reports the raw figure.' },
  { key: 'shoe_pair_width_units', prop: 'shoePairWidthUnits', kind: 'length',
    label: 'Shoe pair width', hint: 'Shelf width one pair of shoes occupies. A section may override it.' },
  { key: 'default_drawer_height_units', prop: 'defaultDrawerHeightUnits', kind: 'length',
    label: 'Drawer height', hint: 'Drawer face height used when a drawer count is stated without a bank to copy.' },
  { key: 'door_min_depth_units', prop: 'doorMinDepthUnits', kind: 'length',
    label: 'Door min depth', hint: 'The shallowest NON-HANGING section that can take doors. The interior editor warns and blocks doors below it.' },
  { key: 'door_min_depth_hanging_units', prop: 'doorMinDepthHangingUnits', kind: 'length',
    label: 'Door min depth (hanging)', hint: 'The shallowest section holding a ROD that can take doors — clothes on a rod need the depth. Applies to the whole section, wherever the rod sits.' },
  { key: 'door_pair_center_reveal_units', prop: 'doorPairCenterRevealUnits', kind: 'length',
    label: 'Door pair reveal', hint: 'Gap drawn between the two leaves of a pair of doors.' },
  { key: 'door_pair_min_width_units', prop: 'doorPairMinWidthUnits', kind: 'length',
    label: 'Door pair min width', hint: 'Openings this wide or wider get a left/right PAIR of doors; narrower ones a single leaf.' },
  { key: 'hang_rod_diameter_units', prop: 'hangRodDiameterUnits', kind: 'length',
    label: 'Hang rod diameter', hint: 'How thick a hang rod draws on the elevation — its real diameter, 1 1/4".' },
  { key: 'rod_hang_floor_clearance_units', prop: 'rodHangFloorClearanceUnits', kind: 'length',
    label: 'Hang length floor clearance', hint: 'When a rod has no board beneath it, HANG LENGTH is measured to this far off the floor — the gap a garment needs so it does not drag. Also the clearance used when one rod hangs directly over another.' },
  { key: 'rod_support_shelf_hole_offset', prop: 'rodSupportShelfHoleOffset', kind: 'plain',
    label: 'Rod support hole offset', hint: 'How many 32mm holes separate a rod’s top from the hole its fixed support shelf is centred on. 2 leaves exactly one visible hole between them — and is the owner’s “about 2 1/2 inches”, exactly.' },
  { key: 'hanging_top_opening_min_units', prop: 'hangingTopOpeningMinUnits', kind: 'length',
    label: 'Double-hang top opening minimum', hint: 'A taller double-hang adds its leftover top opening only at or above this clear height. Smaller remainders stay in the two hang zones.' },
  { key: 'drawer_toe_height_units', prop: 'drawerToeHeightUnits', kind: 'length',
    label: 'Drawer toe height', hint: 'Base trim under the bottom drawer face. Matches the SSH drawer tool.' },
  { key: 'drawer_overlay_units', prop: 'drawerOverlayUnits', kind: 'length',
    label: 'Drawer overlay', hint: 'Drawer face overlay; the reveal at each end of a stack is the shelf thickness minus this. Matches the SSH drawer tool.' },
  { key: 'drawer_vertical_spacing_units', prop: 'drawerVerticalSpacingUnits', kind: 'length',
    label: 'Drawer vertical spacing', hint: 'Air between drawer faces in a bank. Matches the SSH drawer tool.' },
  { key: 'drawer_stack_max_height_units', prop: 'drawerStackMaxHeightUnits', kind: 'length',
    label: 'Drawer stack max height', hint: 'A drawer bank taller than this — including base trim — is warned about, the same threshold the SSH drawer tool uses.' },
  { key: 'drawer_face_side_overlay_units', prop: 'drawerFaceSideOverlayUnits', kind: 'length',
    label: 'Drawer face side overlay', hint: 'How far a drawer face overhangs its opening at EACH side, onto the side panels. A face is its opening plus twice this — 1/4" gives the standard “opening + 1/2”. Separate from the 3/8" vertical drawer overlay above.' },
  { key: 'default_handle_length_mm', prop: 'defaultHandleLengthMm', kind: 'plain',
    label: 'Handle length (mm)', hint: 'OVERALL length of the standard pull, in MILLIMETRES — not centre-to-centre. 96 is the standard stainless bar (3 3/4").' },
  { key: 'handle_two_pull_min_face_width_units', prop: 'handleTwoPullMinFaceWidthUnits', kind: 'length',
    label: 'Two-pull min face width', hint: 'A drawer face this wide OR WIDER takes two pulls (32" itself gets two). Overridden only when the pull is long enough to meet the exception below.' },
  { key: 'handle_single_pull_min_width_fraction', prop: 'handleSinglePullMinWidthFraction', kind: 'plain',
    label: 'Single-pull min width fraction', hint: 'A pull at least this fraction of the FACE width stays a single pull however wide the face is. 0.25 = 25%. With the standard 96mm bar this never fires — it is for long appliance-style bars.' },
  { key: 'elevation_width_label_drop_px', prop: 'elevationWidthLabelDropPx', kind: 'plain',
    label: 'Width label drop', hint: 'Pixels below the elevation floor line the per-section width captions sit, so section bodies never cover them.' },
  { key: 'system_hole_pitch_mm', prop: 'systemHolePitchMm', kind: 'plain',
    label: 'System hole pitch (mm)', hint: 'Vertical spacing of the system holes, in MILLIMETRES. 32 is the system’s name.' },
  { key: 'system_hole_first_offset_mm', prop: 'systemHoleFirstOffsetMm', kind: 'plain',
    label: 'First hole offset (mm)', hint: 'Centre of the FIRST hole above the panel’s BOTTOM EDGE, in millimetres. 9.5 — and a legal panel height ends the same distance above its top hole.' },
  { key: 'system_display_round_units', prop: 'systemDisplayRoundUnits', kind: 'length',
    label: 'System figure rounding', hint: 'Shelf heights, hole positions and shelf-spacing dimensions are SHOWN rounded to this. Display only — placement always uses the exact figure.' },
  { key: 'double_thick_shelf_multiplier', prop: 'doubleThickShelfMultiplier', kind: 'plain',
    label: 'Double-thick multiplier', hint: 'How many standard boards a shelf tagged “double thick” is. 2 gives the 1 1/2" shelf.' },
  { key: 'crown_allowance_units', prop: 'crownAllowanceUnits', kind: 'length',
    label: 'Crown allowance', hint: 'Subtracted from the target height so stacks stop below the crown.' },
  { key: 'crown_moulding_height_units', prop: 'crownMouldingHeightUnits', kind: 'length',
    label: 'Crown moulding height', hint: 'Visible crown height. Its bottom edge sits at the top of the related units.' },
  { key: 'base_trim_height_units', prop: 'baseTrimHeightUnits', kind: 'length',
    label: 'Base trim height', hint: 'Visible base trim height measured up from the floor.' },
  { key: 'base_trim_shelf_overlap_ratio', prop: 'baseTrimShelfOverlapRatio', kind: 'plain',
    label: 'Base trim shelf overlap', hint: 'Fraction of the bottom shelf thickness overlapped by the base trim. One third = 0.333.' },
  { key: 'grid_spacing_units', prop: 'gridSpacingUnits', kind: 'length',
    label: 'Grid spacing', hint: 'The to-scale grid cell on the plan and elevation.' },
  { key: 'default_wall_label_height_units', prop: 'defaultWallLabelHeightUnits', kind: 'length',
    label: 'Wall label height', hint: 'Height a tapped standard label lands at on the elevation.' },
  { key: 'snap_increment_degrees', prop: 'snapIncrementDegrees', kind: 'degrees',
    label: 'Snap increment', hint: 'Cleanup snaps every drawn wall to a multiple of this angle.' },
  { key: 'elevation_scale_px_per_inch', prop: 'elevationScalePxPerInch', kind: 'plain',
    label: 'Elevation scale', hint: 'Pixels per inch the elevation is drawn at before fitting.' },
  { key: 'garage_platform_charge', prop: 'garagePlatformChargePerSection', kind: 'plain',
    label: 'Garage platform $/section', hint: 'Added per section when the space stands on a platform (garage systems). Dollars, flat, no material multiplier.' },
  { key: 'crown_price_per_linear_foot', prop: 'crownPricePerLinearFoot', kind: 'plain',
    label: 'Crown $/linear ft', hint: 'Crown moulding, charged per linear foot of the sections it runs across. Turned on per sketch. Dollars, flat, no material multiplier.' },
  { key: 'base_trim_price_per_linear_foot', prop: 'baseTrimPricePerLinearFoot', kind: 'plain',
    label: 'Base trim $/linear ft', hint: 'Base trim, charged per linear foot. Only sections that HAVE a bottom shelf are counted — switching a section’s bottom shelf off removes its base trim. Dollars, flat, no material multiplier.' },
  { key: 'pricing_range_low_percent', prop: 'pricingRangeLowPercent', kind: 'plain',
    label: 'Price range low %', hint: 'The low end of every price range, as a percent of the computed figure (ballpark convention: 100).' },
  { key: 'pricing_range_high_percent', prop: 'pricingRangeHighPercent', kind: 'plain',
    label: 'Price range high %', hint: 'The high end of every price range, as a percent of the computed figure (ballpark convention: 125).' },
  { key: 'basic_drawer_adder_price_low', prop: 'basicDrawerAdderPriceLow', kind: 'plain',
    label: 'Basic drawer adder $ (low)', hint: 'Dollars per drawer added to a BASIC bucket card, low end. Assumes deco drawer faces. This is the FINAL figure — the price range low % is NOT applied on top of it.' },
  { key: 'basic_drawer_adder_price_high', prop: 'basicDrawerAdderPriceHigh', kind: 'plain',
    label: 'Basic drawer adder $ (high)', hint: 'Dollars per drawer added to a BASIC bucket card, high end. This is the FINAL figure — the price range high % is NOT applied on top of it.' },
]);

/**
 * Versioned resolution for admin-saved LIST rules (SD-118's fifth ruling,
 * 2026-08-29). A v1 row — a plain array — wholesale-replaced the compiled
 * defaults, so any default added after the save was silently gone, and
 * absent was indistinguishable from deliberately-removed. That disabled the
 * owner's laser (bluetooth_target_fields, since removed) and left the SEEDED
 * `section_type_presets` row frozen at its last hand-migration (2026-08-04)
 * while the compiled defaults grew wall beds, fixedHeight and more.
 *
 * v2 envelope: `{version: 2, items, removed}` — items win by id, removed
 * defaults stay gone, anything unmentioned follows the compiled default, so
 * future additions arrive ON with no resave and no migration. A v1 value
 * still resolves WHOLESALE, deliberately: union-on-read was rejected (it
 * resurrects deliberate deletions); the v1 back-catalog is ended by the
 * 20260829180000 migration deleting the stored rows, visibly, under the
 * owner's data-is-disposable ruling.
 *
 * @param {unknown} stored the raw rule_value (v1 array, v2 envelope, or absent)
 * @param {Array<object>} defaults the compiled default list
 * @param {(item: object) => string} idOf identity for merging
 */
export function resolveVersionedListRule(stored, defaults, idOf) {
  if (stored === undefined || stored === null) return defaults;
  if (!Array.isArray(stored) && typeof stored === 'object' && stored.version === 2) {
    const items = Array.isArray(stored.items) ? stored.items : (Array.isArray(stored.rows) ? stored.rows : []);
    const removed = new Set((Array.isArray(stored.removed) ? stored.removed : []).map(String));
    const have = new Set(items.map((item) => String(idOf(item))));
    return [...items, ...defaults.filter((d) => !have.has(String(idOf(d))) && !removed.has(String(idOf(d))))];
  }
  return Array.isArray(stored) ? stored : defaults;
}

/**
 * The one save shape for those rules: `removed` is every default id the
 * editor grid no longer shows. Correct exactly because the migration
 * guarantees a v1-free world — the grid always renders the full resolved
 * set, so a missing id was deleted by the admin, never merely unseen.
 */
export function versionedListSaveValue(items, defaults, idOf, { claimRemovals = true } = {}) {
  const have = new Set(items.map((item) => String(idOf(item))));
  return {
    version: 2,
    items,
    // `claimRemovals: false` is the v1→v2 UPGRADE save (the window between
    // the client deploying and the owner-gated migration running): a v1
    // grid shows the STALE wholesale set and cannot distinguish removed
    // from never-present, so asserting removals there would write every
    // post-save default — the wall beds, their fixedHeight — into the row
    // as DELIBERATE removals, entrenching exactly the staleness this
    // format ends. An upgrade save carries items forward and claims
    // nothing about absence.
    removed: claimRemovals
      ? defaults.map((d) => String(idOf(d))).filter((id) => !have.has(id))
      : [],
  };
}

/** 0 = no stored row, 1 = legacy plain value, 2 = versioned envelope. */
function listRuleVersionOf(stored) {
  if (stored === undefined || stored === null) return 0;
  if (!Array.isArray(stored) && typeof stored === 'object' && stored.version === 2) return 2;
  return 1;
}

export function resolveBusinessRules(rows = {}) {
  const pick = (key, fallback) => (rows[key] === undefined ? fallback : rows[key]);
  const d = DEFAULT_BUSINESS_RULES;
  return Object.freeze({
    snapIncrementDegrees: pick('snap_increment_degrees', d.snapIncrementDegrees),
    wallOneSelectionRule: pick('wall_one_selection_rule', d.wallOneSelectionRule),
    numberingDirection: pick('numbering_direction', d.numberingDirection),
    defaultUnitOfMeasure: pick('default_unit_of_measure', d.defaultUnitOfMeasure),
    closureToleranceUnits: pick('closure_tolerance_units', d.closureToleranceUnits),
    defaultLongestWallUnits: pick('default_longest_wall_units', d.defaultLongestWallUnits),
    defaultCeilingHeightUnits: pick('default_ceiling_height_units', d.defaultCeilingHeightUnits),
    measurementLabelFormat: pick('measurement_label_format', d.measurementLabelFormat),
    elevationScalePxPerInch: pick('elevation_scale_px_per_inch', d.elevationScalePxPerInch),
    elevationPanelOrder: pick('elevation_panel_order', d.elevationPanelOrder),
    defaultDoorWidthUnits: pick('default_door_width_units', d.defaultDoorWidthUnits),
    defaultDoorHeightUnits: pick('default_door_height_units', d.defaultDoorHeightUnits),
    doorSwingLeafAllowanceUnits: pick('door_swing_leaf_allowance_units', d.doorSwingLeafAllowanceUnits),
    bifoldFoldedProjectionUnits: pick('bifold_folded_projection_units', d.bifoldFoldedProjectionUnits),
    defaultWallThicknessUnits: pick('default_wall_thickness_units', d.defaultWallThicknessUnits),
    defaultPartitionLengthUnits: pick('default_partition_length_units', d.defaultPartitionLengthUnits),
    atticLadderCeilingInsetUnits: pick('attic_ladder_ceiling_inset_units', d.atticLadderCeilingInsetUnits),
    atticLadderSideClearanceUnits: pick('attic_ladder_side_clearance_units', d.atticLadderSideClearanceUnits),
    wallLabelPresets: pick('wall_label_presets', d.wallLabelPresets),
    defaultWallLabelHeightUnits: pick('default_wall_label_height_units', d.defaultWallLabelHeightUnits),
    sectionEndRevealUnits: pick('section_end_reveal_units', d.sectionEndRevealUnits),
    sectionPanelThicknessUnits: pick('section_panel_thickness_units', d.sectionPanelThicknessUnits),
    sectionMinOpeningUnits: pick('section_min_opening_units', d.sectionMinOpeningUnits),
    sectionMaxOpeningUnits: pick('section_max_opening_units', d.sectionMaxOpeningUnits),
    sectionDefaultDepthUnits: pick('section_default_depth_units', d.sectionDefaultDepthUnits),
    abuttingPanelReachUnits: pick('abutting_panel_reach_units', d.abuttingPanelReachUnits),
    sectionHangingCodes: pick('section_hanging_codes', d.sectionHangingCodes),
    sectionHangClearanceUnits: pick('section_hang_clearance_units', d.sectionHangClearanceUnits),
    sectionCornerGapUnits: pick('section_corner_gap_units', d.sectionCornerGapUnits),
    planOpenSpanLabelMinUnits: pick('plan_open_span_label_min_units', d.planOpenSpanLabelMinUnits),
    hangCategories: pick('hang_categories', d.hangCategories),
    sectionOpenCodes: pick('section_open_codes', d.sectionOpenCodes),
    shoePairWidthUnits: pick('shoe_pair_width_units', d.shoePairWidthUnits),
    drawerBankPresets: pick('drawer_bank_presets', d.drawerBankPresets),
    defaultDrawerHeightUnits: pick('default_drawer_height_units', d.defaultDrawerHeightUnits),
    drawerFaceHeights: pick('drawer_face_heights', d.drawerFaceHeights),
    drawerToeHeightUnits: pick('drawer_toe_height_units', d.drawerToeHeightUnits),
    drawerOverlayUnits: pick('drawer_overlay_units', d.drawerOverlayUnits),
    drawerVerticalSpacingUnits: pick('drawer_vertical_spacing_units', d.drawerVerticalSpacingUnits),
    drawerStackMaxHeightUnits: pick('drawer_stack_max_height_units', d.drawerStackMaxHeightUnits),
    // Handles (ADR 0064). The face's own side overlay, the type catalog, and
    // the two-pull rule with its 25% exception.
    drawerFaceSideOverlayUnits: pick('drawer_face_side_overlay_units', d.drawerFaceSideOverlayUnits),
    handleTypes: pick('handle_types', d.handleTypes),
    defaultHandleType: pick('default_handle_type', d.defaultHandleType),
    defaultHandleLengthMm: pick('default_handle_length_mm', d.defaultHandleLengthMm),
    handleTwoPullMinFaceWidthUnits: pick('handle_two_pull_min_face_width_units', d.handleTwoPullMinFaceWidthUnits),
    handleSinglePullMinWidthFraction: pick('handle_single_pull_min_width_fraction', d.handleSinglePullMinWidthFraction),
    handlePairPositions: pick('handle_pair_positions', d.handlePairPositions),
    rodSupportShelfHoleOffset: pick('rod_support_shelf_hole_offset', d.rodSupportShelfHoleOffset),
    rodHangFloorClearanceUnits: pick('rod_hang_floor_clearance_units', d.rodHangFloorClearanceUnits),
    hangRodDiameterUnits: pick('hang_rod_diameter_units', d.hangRodDiameterUnits),
    rodSupportShelfGapUnits: pick('rod_support_shelf_gap_units', d.rodSupportShelfGapUnits),
    doorMinDepthUnits: pick('door_min_depth_units', d.doorMinDepthUnits),
    doorMinDepthHangingUnits: pick('door_min_depth_hanging_units', d.doorMinDepthHangingUnits),
    doorPairCenterRevealUnits: pick('door_pair_center_reveal_units', d.doorPairCenterRevealUnits),
    doorPairMinWidthUnits: pick('door_pair_min_width_units', d.doorPairMinWidthUnits),
    elevationWidthLabelDropPx: pick('elevation_width_label_drop_px', d.elevationWidthLabelDropPx),
    storageRoundingUnits: pick('storage_rounding_units', d.storageRoundingUnits),
    sectionDefaultHeightUnits: pick('section_default_height_units', d.sectionDefaultHeightUnits),
    // Provenance for the admin saves: a v1-loaded grid must not claim
    // removals (see versionedListSaveValue) — the saves read these.
    listRuleVersions: {
      section_type_presets: listRuleVersionOf(rows['section_type_presets']),
      obstruction_types: listRuleVersionOf(rows['obstruction_types']),
      cv_type_mapping: listRuleVersionOf(rows['cv_type_mapping']),
      door_styles: listRuleVersionOf(rows['door_styles']),
    },
    sectionTypePresets: resolveVersionedListRule(rows['section_type_presets'], d.sectionTypePresets, (p) => p.code),
    obstructionQuickKeys: pick('obstruction_quick_keys', d.obstructionQuickKeys),
    gridSpacingUnits: pick('grid_spacing_units', d.gridSpacingUnits),
    planDepthLinePresets: pick('plan_depth_line_presets', d.planDepthLinePresets),
    platformHeights: pick('platform_heights', d.platformHeights),
    countertopMaterialPresets: pick('countertop_material_presets', d.countertopMaterialPresets),
    crownAllowanceUnits: pick('crown_allowance_units', d.crownAllowanceUnits),
    crownMouldingHeightUnits: pick('crown_moulding_height_units', d.crownMouldingHeightUnits),
    baseTrimHeightUnits: pick('base_trim_height_units', d.baseTrimHeightUnits),
    baseTrimShelfOverlapRatio: pick('base_trim_shelf_overlap_ratio', d.baseTrimShelfOverlapRatio),
    sectionShelfThicknessUnits: pick('section_shelf_thickness_units', d.sectionShelfThicknessUnits),
    // The 32 mm system (ADR 0061).
    systemHolePitchMm: pick('system_hole_pitch_mm', d.systemHolePitchMm),
    systemHoleFirstOffsetMm: pick('system_hole_first_offset_mm', d.systemHoleFirstOffsetMm),
    systemDisplayRoundUnits: pick('system_display_round_units', d.systemDisplayRoundUnits),
    snapPanelHeightsToSystem: pick('snap_panel_heights_to_system', d.snapPanelHeightsToSystem),
    doubleThickShelfMultiplier: pick('double_thick_shelf_multiplier', d.doubleThickShelfMultiplier),
    floorShelfGapUnits: pick('floor_shelf_gap_units', d.floorShelfGapUnits),
    hangingTopOpeningMinUnits: pick('hanging_top_opening_min_units', d.hangingTopOpeningMinUnits),
    elevationReferenceLines: pick('elevation_reference_lines', d.elevationReferenceLines),
    obstructionTypes: resolveVersionedListRule(rows['obstruction_types'], d.obstructionTypes, (t) => t.key),
    doorStyles: resolveVersionedListRule(rows['door_styles'], d.doorStyles, (style) => style.key),
    // CV pricing (ADR 0058, plan §5/4e + §1.4 admin-maintained data).
    cvSectionCatalog: pick('cv_section_catalog', d.cvSectionCatalog),
    cvMaterialMultipliers: pick('cv_material_multipliers', d.cvMaterialMultipliers),
    cvCountertops: pick('cv_countertops', d.cvCountertops),
    cvAccessories: pick('cv_accessories', d.cvAccessories),
    cvTypeMapping: (() => {
      // Same versioned semantics, wrapped: the value is an object carrying
      // `rows` (plus siblings like heightAliases), so the list resolution
      // applies to the rows while other fields keep last-wins.
      const stored = rows['cv_type_mapping'];
      if (stored === undefined || stored === null) return d.cvTypeMapping;
      if (typeof stored === 'object' && !Array.isArray(stored) && stored.version === 2) {
        const { version, removed, ...rest } = stored;
        return {
          ...d.cvTypeMapping,
          ...rest,
          rows: resolveVersionedListRule(stored, d.cvTypeMapping.rows, (r) => r.code),
        };
      }
      return stored; // v1: wholesale, ended by the 20260829180000 migration
    })(),
    // Basic mode buckets (ADR 0059) — definitions, availability, defaults.
    basicBuckets: pick('basic_buckets', d.basicBuckets),
    basicBucketAvailability: pick('basic_bucket_availability', d.basicBucketAvailability),
    basicSpaceTypes: pick('basic_space_types', d.basicSpaceTypes),
    basicDefaultBounds: pick('basic_default_bounds', d.basicDefaultBounds),
    quickCoreSectionBucketMap: pick('quick_core_section_bucket_map', d.quickCoreSectionBucketMap),
    garagePlatformChargePerSection: pick('garage_platform_charge', d.garagePlatformChargePerSection),
    crownPricePerLinearFoot: pick('crown_price_per_linear_foot', d.crownPricePerLinearFoot),
    baseTrimPricePerLinearFoot: pick('base_trim_price_per_linear_foot', d.baseTrimPricePerLinearFoot),
    basicDrawerAdderPriceLow: pick('basic_drawer_adder_price_low', d.basicDrawerAdderPriceLow),
    basicDrawerAdderPriceHigh: pick('basic_drawer_adder_price_high', d.basicDrawerAdderPriceHigh),
    pricingRangeLowPercent: pick('pricing_range_low_percent', d.pricingRangeLowPercent),
    pricingRangeHighPercent: pick('pricing_range_high_percent', d.pricingRangeHighPercent),
  });
}
